import React from "react";
import {translate} from "react-i18nify";

import * as componentTypes from "../constants/componentTypes";
import * as config from "../constants/config";
import * as itemTypes from "../constants/itemTypes";
import * as statsTypes from "../constants/statsTypes";
import * as rcc from "../constants/rcc";
import * as settings from "../constants/settings";
import * as databaseStatuses from "../constants/databaseStatuses";
import * as helper from "../constants/helper";
import * as setup from "../constants/setup";
import * as resources from "../constants/resources";
import * as priceTypes from "../constants/priceTypes";
import * as modules from "../constants/modules";
import * as translationHelper from "./translationHelper";
import * as formatHelper from "./formatHelper";
import * as getterHelper from "./getterHelper";
import * as responseHelper from "./responseHelper";
import * as validationHelper from "./validationHelper";
import * as conversionHelper from "./conversionHelper";
import * as windowService from "../services/windowService";

import Autotask from "../models/entities/Autotask";
import Barcode from "../models/entities/Barcode";
import Base from "../models/entities/Base";
import BaseInProductPackage from "../models/entities/BaseInProductPackage";
import BaseInProductPackagePriceData from "../models/utils/BaseInProductPackagePriceData";
import BaseInProductPriceData from "../models/utils/BaseInProductPriceData";
import BaseInProductPricingTable from "../models/utils/BaseInProductPricingTable";
import BaseInProductPackagePricingTable from "../models/utils/BaseInProductPackagePricingTable";
import Canister from "../models/entities/Canister";
import Color from "../models/entities/Color";
import ColorDifferenceData from "../models/utils/ColorDifferenceData";
import Colorfile from "../models/entities/Colorfile";
import ColorfileTemplate from "../models/entities/ColorfileTemplate";
import ColorInFandeck from "../models/entities/ColorInFandeck";
import Colorant from "../models/entities/Colorant";
import ColorantBatchNumber from "../models/entities/ColorantBatchNumber";
import ColorantPackage from "../models/entities/ColorantPackage";
import ColorantPriceData from "../models/utils/ColorantPriceData";
import ColorantPricingTable from "../models/utils/ColorantPricingTable";
import Currency from "../models/entities/Currency";
import Customer from "../models/entities/Customer";
import Database from "../models/utils/Database";
import Dialog from "../models/gui/Dialog";
import DownloadingFile from "../models/utils/DownloadingFile";
import Fandeck from "../models/entities/Fandeck";
import FileFormat from "../models/utils/FileFormat";
import FileTransferInTask from "../models/utils/FileTransferInTask";
import FileTransferOutTask from "../models/utils/FileTransferOutTask";
import FlashMessage from "../models/gui/FlashMessage";
import FormatDataImport from "../models/utils/FormatDataImport";
import FormatOption from "../models/utils/FormatOption";
import Formula from "../models/entities/Formula";
import FormulaMoreInfo from "../models/entities/FormulaMoreInfo";
import FormulaValidation from "../models/utils/FormulaValidation";
import HelperConfiguration from "../models/utils/HelperConfiguration";
import InfoDialog from "../models/gui/InfoDialog";
import KeyboardShortcut from "../models/utils/KeyboardShortcut";
import LicenseContactPerson from "../models/gui/LicenseContactPerson";
import LicenseMachineInformation from "../models/gui/LicenseMachineInformation";
import LicensePointOfSale from "../models/gui/LicensePointOfSale";
import LicenseProduct from "../models/utils/LicenseProduct";
import LinearListObject from "../models/utils/LinearListObject";
import LinearListObjectItem from "../models/utils/LinearListObjectItem";
import LoggedUser from "../models/utils/LoggedUser";
import MachineActionProgress from "../models/gui/MachineActionProgress";
import MachineColorant from "../models/entities/MachineColorant";
import MainDatabase from "../models/utils/MainDatabase";
import ManualColorant from "../models/entities/ManualColorant";
import Margin from "../models/entities/Margin";
import MarginData from "../models/entities/MarginData";
import MatchingBase from "../models/entities/MatchingBase";
import MatchingData from "../models/utils/MatchingData";
import MenuItem from "../models/gui/MenuItem";
import MessageDialog from "../models/gui/MessageDialog";
import Node from "../models/utils/Node";
import Order from "../models/entities/Order";
import OrderData from "../models/entities/OrderData";
import Package from "../models/entities/Package";
import PackagePriceData from "../models/utils/PackagePriceData";
import PaletteColor from "../models/entities/PaletteColor";
import PosForm from "../models/gui/PosForm";
import Price from "../models/entities/Price";
import PriceColorants from "../models/entities/PriceColorants";
import PriceData from "../models/entities/PriceData";
import PriceGroup from "../models/entities/PriceGroup";
import PriceString from "../models/entities/PriceString";
import Printer from "../models/utils/Printer";
import PrinterTemplate from "../models/entities/PrinterTemplate";
import PrintTask from "../models/utils/PrintTask";
import Product from "../models/entities/Product";
import ProductFile from "../models/entities/ProductFile";
import ProgressDialog from "../models/gui/ProgressDialog";
import ColorantConsumption from "../models/entities/ColorantConsumption";
import RefillHistoryRecord from "../models/entities/RefillHistoryRecord";
import RegistrationForm from "../models/gui/RegistrationForm";
import SearchPanelColor from "../models/entities/SearchPanelColor";
import Server from "../models/utils/Server";
import ServiceDepartment from "../models/entities/ServiceDepartment";
import SettingsItem from "../models/gui/SettingsItem";
import SmartPurgeRule from "../models/entities/SmartPurgeRule";
import SpectralData from "../models/utils/SpectralData";
import StatisticCorrection from "../models/entities/StatisticCorrection";
import Statistics from "../models/entities/Statistics";
import Step from "../models/gui/Step";
import StockItem from "../models/utils/StockItem";
import User from "../models/entities/User";
import Unit from "../models/entities/Unit";
import UserGroup from "../models/entities/UserGroup";
import Tab from "../models/gui/Tab";
import Ticket from "../models/entities/Ticket";
import TintometricSystem from "../models/entities/TintometricSystem";
import TableCell from "../models/gui/TableCell";
import TableRow from "../models/gui/TableRow";
import TableColumn from "../models/gui/TableColumn";
import ModalSection from "../models/gui/ModalSection";
import ColorantPackageStock from "../models/utils/ColorantPackageStock";
import DropdownToggleButton from "../models/gui/DropdownToggleButton";
import Module from "../models/utils/Module";
import SelectValue from "../models/gui/SelectValue";

export const mapColorDataToObject = (
    id,
    name,
    displayName,
    code,
    rgb = config.DEFAULT_RGB,
    local = false,
    hasFormula = true,
    rgbOverride = false
) => {
    try {
        return new SearchPanelColor(id, name, displayName, code, rgb, local, hasFormula, rgbOverride);
    } catch (e) {
        windowService.logError("BA02121EDD7BF8F", e.message);
        return null;
    }
};

export const mapPaletteColorDataToObject = (
    id,
    name,
    displayName,
    code,
    rgb = config.DEFAULT_RGB,
    local = false,
    hasFormula = true,
    cifId = null,
    fandeckId = null,
    moreInfoData = null,
    hasImage = false,
    hasReflData = false
) => {
    try {
        return new PaletteColor(
            id,
            name,
            displayName,
            code,
            rgb,
            local,
            hasFormula,
            cifId,
            fandeckId,
            moreInfoData,
            hasImage,
            hasReflData
        );
    } catch (e) {
        windowService.logError("009313CC677EC79", e.message);
        return null;
    }
};

export const mapColorToObject = (data) => {
    if (data === null) {
        return null;
    }

    try {
        return mapColorDataToObject(
            data.id,
            data.name,
            data.displayName === undefined || data.displayName === null ? data.name : data.displayName,
            data.code,
            data.rgb,
            data.local === undefined && data.isLocal === undefined
                ? false
                : data.local === true || data.isLocal === true
        );
    } catch (e) {
        windowService.logError("B4E4CB24A27485D", e.message);
        return null;
    }
};

export const mapFormulaToObject = (formulaId, id, name, displayName, code, rgb = config.DEFAULT_RGB, local = false) => {
    try {
        return new Formula(formulaId, new Color(id, name, displayName, code, rgb, local, true));
    } catch (e) {
        windowService.logError("8FDA69193EF15E2", e.message);
        return null;
    }
};

export const mapCustomFormulaToObject = (rgb = config.DEFAULT_RGB) => {
    try {
        return mapFormulaToObject(0, 0, null, null, null, rgb, true);
    } catch (e) {
        windowService.logError("5DC30104B15ff54", e.message);
        return null;
    }
};

export const serializeColor = (color) => {
    try {
        return Color.serialize(color);
    } catch (e) {
        windowService.logError("AC83F5EA639C321", e.message);
        return null;
    }
};

export const unserializeColor = (color) => {
    try {
        return Color.unserialize(color);
    } catch (e) {
        windowService.logError("BCBCDE4436813DE", e.message);
        return null;
    }
};

export const serializeFandeck = (fandeck) => {
    try {
        return Fandeck.serialize(fandeck);
    } catch (e) {
        windowService.logError("22A1CE5BE69238C", e.message);
        return null;
    }
};

export const unserializeFandeck = (fandeck) => {
    try {
        return Fandeck.unserialize(fandeck);
    } catch (e) {
        windowService.logError("5CE894C57086696", e.message);
        return null;
    }
};

export const serializeProduct = (product) => {
    try {
        return Product.serialize(product);
    } catch (e) {
        windowService.logError("37A411734B69991", e.message);
        return null;
    }
};

export const unserializeProduct = (product) => {
    try {
        return Product.unserialize(product);
    } catch (e) {
        windowService.logError("DD32EDDAF78159C", e.message);
        return null;
    }
};

export const serializeBase = (base) => {
    try {
        return Base.serialize(base);
    } catch (e) {
        windowService.logError("3B1D11548E0556B", e.message);
        return null;
    }
};

export const unserializeBase = (base) => {
    try {
        return Base.unserialize(base);
    } catch (e) {
        windowService.logError("78AF7C8403CA047", e.message);
        return null;
    }
};

export const mapLastDispensedFormulaToObject = (item) => {
    let combination = null;

    try {
        combination = {
            product: mapProductToObject(item.product),
            fandeck: mapFandeckToObject(item.fandeck),
            color: mapColorToObject(item.color),
            base: mapBaseToObject(item.base)
        };

        if (combination.color !== null) {
            combination.color.local = item.isLocal;
        }
    } catch (e) {
        windowService.logError("829376FD5846F57", e.message);
    } finally {
        if (
            combination.product !== null &&
            combination.fandeck !== null &&
            combination.color !== null &&
            combination.base !== null
        ) {
            return combination;
        }

        return null;
    }
};

export const mapLastDispensedFormulasToObjects = (list) => {
    let formulas = [];
    let combination = null;

    try {
        for (let item of list) {
            combination = mapLastDispensedFormulaToObject(item);

            if (combination !== null) {
                formulas.push(combination);
            }
        }
    } catch (e) {
        windowService.logError("F78B2837CFED87B", e.message);
        return [];
    }

    return formulas;
};

export const mapColorCombinationsToObjects = (list) => {
    let formulas = [];

    try {
        let item;
        for (item of list) {
            try {
                formulas.push({
                    product: new Product(item.productId, item.productName, item.productDisplayName),
                    fandeck: new Fandeck(item.fandeckId, item.fandeckName, item.fandeckDisplayName, item.isLocal),
                    formula: mapColorDataToObject(
                        item.colorId,
                        item.colorName,
                        item.colorDisplayName,
                        item.colorCode,
                        item.rgb,
                        item.isLocal
                    )
                });
            } catch (e) {
                windowService.logError("FBA2271317E9257", e.message);
            }
        }
    } catch (e) {
        windowService.logError("ABD149BB604BEE7", e.message);
        return [];
    }

    return formulas;
};

const buildNode = (id, name, parentId = null, nodes = [], items = []) => {
    try {
        return new Node(id, name, parentId, nodes, items);
    } catch (e) {
        windowService.logError("9C7553AA77D1EDF", e.message);
        return null;
    }
};

const isDuplicate = (item, list) => {
    let oldItem;
    for (oldItem of list) {
        if (
            oldItem.product === item.product &&
            oldItem.fandeck === item.fandeck &&
            oldItem.color === item.color &&
            oldItem.base === item.base
        ) {
            return true;
        }
    }
    return false;
};

const searchAndMapFavorites = (list, oldAddedItems = []) => {
    let groups = [];
    let addedItems = oldAddedItems;
    if (list.nodes !== undefined) {
        let node, group, item;
        for (node of list.nodes) {
            group = buildNode(node.id, node.name, node.parentId, searchAndMapFavorites(node, addedItems));

            for (item of node.items) {
                let newItem;
                if (
                    item.color !== null &&
                    item.product !== null &&
                    item.fandeck !== null &&
                    item.base !== null &&
                    item.color !== undefined &&
                    item.product !== undefined &&
                    item.fandeck !== undefined &&
                    item.base !== undefined
                ) {
                    newItem = {
                        product: item.product.id,
                        fandeck: item.fandeck.id,
                        color: item.color.id,
                        base: item.base.id
                    };

                    if (!isDuplicate(newItem, addedItems)) {
                        try {
                            group.addItem({
                                favoriteId: item.id,
                                node: node.id,
                                product: new Product(
                                    item.product.id,
                                    item.product.name,
                                    item.product.displayName,
                                    item.product.code
                                ),
                                fandeck: new Fandeck(
                                    item.fandeck.id,
                                    item.fandeck.name,
                                    item.fandeck.displayName,
                                    item.fandeck.isLocal
                                ),
                                color: mapColorDataToObject(
                                    item.color.id,
                                    item.color.name,
                                    item.color.displayName,
                                    item.color.code,
                                    item.color.rgb,
                                    item.color.isLocal
                                ),
                                base: new Base(item.base.id, item.base.name, item.base.displayName)
                            });
                        } catch (e) {
                            windowService.logError("92937901E26312D", e.message);
                        } finally {
                            addedItems.push(newItem);
                        }
                    }
                }
            }

            groups.push(group);
        }
    }

    return groups;
};

export const mapFavoritesToObjects = (list) => {
    let favorites = null;

    try {
        favorites = searchAndMapFavorites({
            nodes: [buildNode(0, "Root", null, list.nodes, list.items)]
        });
    } catch (e) {
        windowService.logError("9ED6DA36BDAF057", e.message);
        return [buildNode(0, "Root")];
    }

    return favorites;
};

export const mapColorantToObject = (colorant) => {
    try {
        return new Colorant(
            colorant.id,
            colorant.name,
            colorant.displayName,
            colorant.rgb,
            0,
            settings.UNIT_ML,
            colorant.density === null ? 1 : colorant.density,
            colorant.ufiCode
        );
    } catch (e) {
        windowService.logError("3C45AD725863B33", e.message);
    }

    return null;
};

export const mapPackageToObject = (data, filling = 0, fillingMl = 0) => {
    try {
        return new Package(
            data.id,
            data.name,
            data.displayName,
            data.unitId,
            window.storeProvider.getUnitCode(data.unitId),
            data.nominalAmount,
            filling,
            fillingMl,
            data.isGravimetric
        );
    } catch (e) {
        windowService.logError("6E16D7B4D1C1EF1", e.message);
        return null;
    }
};

export const mapColorantsToObjects = (list) => {
    let colorants = [];

    try {
        let colorant;
        let colorantObject;
        for (colorant of list) {
            colorantObject = mapColorantToObject(colorant);
            if (colorantObject) {
                colorants.push(colorantObject);
            }
        }
    } catch (e) {
        windowService.logError("C0DF64C33FFA07A", e.message);
        return [];
    }

    return colorants;
};

export const mapPriceDataToObjects = (list, rate = 1) => {
    if (list === undefined || list === null) {
        return [];
    }

    let prices = [];

    try {
        let item;
        for (item of list) {
            try {
                switch (item.valueType) {
                    case priceTypes.VALUE_TYPE_ABSOLUTE:
                        prices.push(
                            new PriceData(
                                item.priceType,
                                item.priceTypeName,
                                item.valueType,
                                item.defaultValue === null ? 0 : item.defaultValue * rate,
                                item.customValue === null ? 0 : item.customValue * rate,
                                item.value === null ? 0 : item.value * rate,
                                item.priceGroup,
                                item.editable,
                                list.find(
                                    (record) =>
                                        record.priceTypeId === priceTypes.PRICE_GROUP_NAME &&
                                        record.priceGroup === item.priceGroup &&
                                        item.priceTypeId === priceTypes.PRICE_GROUP_TYPE
                                )?.value ?? null
                            )
                        );
                        break;
                    case priceTypes.VALUE_TYPE_PERCENT:
                        prices.push(
                            new PriceData(
                                item.priceType,
                                item.priceTypeName,
                                item.valueType,
                                item.defaultValue === null
                                    ? 0
                                    : formatHelper.convertDecimalToPercent(item.defaultValue),
                                item.customValue === null ? 0 : formatHelper.convertDecimalToPercent(item.customValue),
                                item.value === null ? 0 : formatHelper.convertDecimalToPercent(item.value),
                                item.priceGroup,
                                item.editable
                            )
                        );
                        break;
                    case priceTypes.BASE_SELL_FULL:
                    case priceTypes.CNT_SELL_FULL:
                    case priceTypes.CNT_SELL_FULL_BY_NAME:
                    case priceTypes.TINT_SELL_FULL_ALL:
                    case priceTypes.TINT_SELL_FULL_PER_PKG:
                    case priceTypes.TOTAL_SELL_FULL_ALL:
                    case priceTypes.TOTAL_SELL_FULL_PER_L:
                    case priceTypes.TOTAL_SELL_FULL_PER_KG:
                    case priceTypes.TOTAL_SELL_FULL_PER_PKG:
                        break;
                    default:
                        prices.push(
                            new PriceData(
                                item.priceType,
                                item.priceTypeName,
                                item.valueType,
                                item.defaultValue === null ? 0 : item.defaultValue,
                                item.customValue === null ? 0 : item.customValue,
                                item.value === null ? 0 : item.value,
                                item.priceGroup,
                                item.editable
                            )
                        );
                        break;
                }
            } catch (e) {
                windowService.logError("277F52D1001595B", e.message);
            }
        }
    } catch (e) {
        windowService.logError("AA62118478F1BEC", e.message);
        return [];
    }

    return prices;
};

const mapMarginsToTableData = (margins) => {
    if (margins === undefined || margins === null) {
        return {};
    }

    let tableData = {};

    try {
        for (let margin of margins) {
            tableData[margin.local ? "local-" + margin.marginId : "default-" + margin.marginId] = String(
                formatHelper.convertDecimalToPercent(margin.value)
            );
        }
    } catch (e) {
        windowService.logError("8595A5929F984CB", e.message);
        return {};
    }

    return tableData;
};

export const mapColorantPriceDataToObjects = (list, rate, unit) => {
    let colorantPriceData = [];

    try {
        let prices = null;

        for (let colorant of list) {
            try {
                prices = mapPriceDataToObjects(colorant.prices, rate);

                colorantPriceData.push(
                    new ColorantPriceData(
                        colorant.id,
                        colorant.displayName,
                        colorant.rgb,
                        getterHelper.getPriceObjectByType(
                            prices,
                            unit === settings.UNIT_KG_ID
                                ? priceTypes.PURCHASE_PER_KILOGRAM
                                : priceTypes.PURCHASE_PER_LITER
                        ),
                        getterHelper.getPriceObjectByType(
                            prices,
                            unit === settings.UNIT_KG_ID ? priceTypes.SELL_PER_KILOGRAM : priceTypes.SELL_PER_LITER
                        ),
                        getterHelper.getPriceObjectByType(prices, priceTypes.VAT),
                        getterHelper.getPriceObjectByType(prices, priceTypes.PURCHASE_PRICE_GROUP_PER_LITER),
                        mapMarginsToTableData(colorant.margins),
                        getterHelper.getPriceObjectByType(prices, priceTypes.POS_COEFICIENT)
                    )
                );
            } catch (e) {
                windowService.logError("6289B41EF37EF45", e.message);
            }
        }
    } catch (e) {
        windowService.logError("31CF053EF1AF479", e.message);
        return [];
    }

    return colorantPriceData;
};

export const mapBaseInProductsPriceDataToObjects = (list, rate, unit) => {
    let baseInProductPriceData = [];

    try {
        let prices = null;

        for (let baseInProduct of list) {
            try {
                prices = mapPriceDataToObjects(baseInProduct.prices, rate);

                baseInProductPriceData.push(
                    new BaseInProductPriceData(
                        baseInProduct.id,
                        baseInProduct.baseDisplayName,
                        getterHelper.getPriceObjectByType(
                            prices,
                            unit === settings.UNIT_KG_ID
                                ? priceTypes.PURCHASE_PER_KILOGRAM
                                : priceTypes.PURCHASE_PER_LITER
                        ),
                        getterHelper.getPriceObjectByType(
                            prices,
                            unit === settings.UNIT_KG_ID ? priceTypes.SELL_PER_KILOGRAM : priceTypes.SELL_PER_LITER
                        ),
                        getterHelper.getPriceObjectByType(prices, priceTypes.VAT),
                        mapMarginsToTableData(baseInProduct.margins),
                        getterHelper.getPriceObjectByType(prices, priceTypes.POS_COEFICIENT)
                    )
                );
            } catch (e) {
                windowService.logError("BE41CD688D41FB3", e.message);
            }
        }
    } catch (e) {
        windowService.logError("B34A8914128DEF0", e.message);
        return [];
    }

    return baseInProductPriceData;
};

export const mapBaseInProductPackagesPriceDataToObjects = (list, rate) => {
    let result = [];

    try {
        let prices = null;
        let product = null;
        let packageP = null;
        for (product of list) {
            try {
                for (packageP of product.packages) {
                    prices = mapPriceDataToObjects(packageP.prices, rate);

                    result.push(
                        new BaseInProductPackagePriceData(
                            packageP.id,
                            product.baseDisplayName,
                            packageP.displayName,
                            getterHelper.getPriceObjectByType(prices, priceTypes.PURCHASE),
                            getterHelper.getPriceObjectByType(prices, priceTypes.SELL),
                            getterHelper.getPriceObjectByType(prices, priceTypes.VAT),
                            getterHelper.getPriceObjectByType(prices, priceTypes.SURCHARGE),
                            mapMarginsToTableData(packageP.margins),
                            getterHelper.getPriceObjectByType(prices, priceTypes.POS_COEFICIENT)
                        )
                    );
                }
            } catch (e) {
                windowService.logError("4C1BEEFFF9B30E1", e.message);
            }
        }
    } catch (e) {
        windowService.logError("6E0A0F007517513", e.message);
        return [];
    }

    return result;
};

export const mapPackagePriceDataToObjects = (list, rate) => {
    if (list === null) {
        return [];
    }

    let result = [];

    try {
        let prices;
        for (let item of list) {
            try {
                prices = mapPriceDataToObjects(item.prices, rate);

                result.push(
                    new PackagePriceData(
                        item.id,
                        item.displayName,
                        getterHelper.getPriceObjectByType(prices, priceTypes.SURCHARGE)
                    )
                );
            } catch (e) {
                windowService.logError("C0D52AD12E376A4", e.message);
            }
        }
    } catch (e) {
        windowService.logError("4A98C51D9B912FE", e.message);
        return [];
    }

    return result;
};

const mapProductsInGroupsToObjects = (list) => {
    let groups = [];

    try {
        if (list.nodes === undefined) {
            return [];
        }

        let node, group, item;
        for (node of list.nodes) {
            try {
                group = buildNode(node.id, node.displayName, node.parentId, mapProductsInGroupsToObjects(node));

                if (node.items !== undefined) {
                    for (item of node.items) {
                        if (item.name !== null) {
                            try {
                                group.addItem(
                                    new Product(
                                        item.id,
                                        item.name,
                                        item.displayName,
                                        item.code,
                                        node.displayName,
                                        item.enabled
                                    )
                                );
                            } catch (e) {
                                windowService.logError("E35AD0625701BCD", e.message);
                            }
                        }
                    }
                }

                groups.push(group);
            } catch (e) {
                windowService.logError("84284F5F8E92416", e.message);
            }
        }
    } catch (e) {
        windowService.logError("319FE82F99F3BB8", e.message);
        return [];
    }

    return groups;
};

export const mapProductsToObjects = (list) => {
    let groups = [];

    if (list.items.length > 0) {
        let group = buildNode(0, "");

        for (let item of list.items) {
            try {
                if (item.name !== null) {
                    group.addItem(
                        new Product(item.id, item.name, item.displayName, item.code, group.displayName, item.enabled)
                    );
                }
            } catch (e) {
                windowService.logError("48C46EE987658D4", e.message);
            }
        }

        if (group.items.length || group.nodes.length) {
            groups.push(group);
        }
    }

    groups = groups.concat(mapProductsInGroupsToObjects(list));

    return groups;
};

export const mapProductsWithoutGroupsToObjects = (list) => {
    let group = buildNode(0, "");

    try {
        for (let item of list) {
            if (item !== null) {
                group.addItem(new Product(item.id, item.name, item.displayName, item.code, "", item.enabled));
            }
        }
    } catch (e) {
        windowService.logError("206B406217050CC", e.message);
    }

    return [group];
};

export const processTreeToLinearList = (tree, type, groupIndex = 0, itemIndex = 0) => {
    let list = [];

    let group, item, key, groupItems, childItems;
    for (group of tree) {
        key = type + "-" + group.id;
        groupItems = [];

        for (item of group.items) {
            groupItems.push(
                new LinearListObjectItem(
                    item.id,
                    key + "-" + item.id,
                    itemIndex,
                    item.displayName,
                    item.code === undefined ? null : item.code,
                    false
                )
            );
            itemIndex++;
        }

        if (groupItems.length > 0) {
            groupItems[groupItems.length - 1].last = true;

            if (group.name !== "") {
                list.push(new LinearListObject(group.id, key, groupIndex, group.name));
                groupIndex++;
            }

            for (item of groupItems) {
                list.push(item);
            }
        }

        if (group.nodes.length > 0) {
            childItems = processTreeToLinearList(group.nodes, type, groupIndex, itemIndex);

            groupIndex = childItems.groupIndex;
            itemIndex = childItems.itemIndex;

            if (childItems.list.length > 0) {
                for (item of childItems.list) {
                    list.push(item);
                }
            }
        }
    }

    return {
        list: list,
        groupIndex: groupIndex,
        itemIndex: itemIndex
    };
};

export const mapProductsToSimpleList = (productList) => {
    let result = [];

    try {
        for (let product of productList) {
            result.push(new Product(product.id, product.name, product.displayName, product.code, "", product.enabled));
        }
    } catch (e) {
        windowService.logError("AB33D33960E175E", e.message);
    }

    return result;
};

export const mapMasterProductsToObjects = (list) => {
    let masterProducts = [];

    try {
        for (let item of list) {
            try {
                masterProducts.push(new Product(item.id, item.name, item.displayName, item.code, item.nameMaster));
            } catch (e) {
                windowService.logError("D5ACC003FB8E99F", e.message);
            }
        }
    } catch (e) {
        windowService.logError("C1CBB293F2D5B90", e.message);
        return [];
    }

    return masterProducts;
};

export const mapSimpleProductsToObjects = (list) => {
    let items = [];
    let processed = {};

    try {
        let node, item;
        for (node of list.nodes) {
            for (item of node.items) {
                if (processed[item.id] === undefined) {
                    items.push(item);
                    processed[item.id] = null;
                }
            }
        }

        for (item of list.items) {
            if (processed[item.id] === undefined) {
                items.push(item);
                processed[item.id] = null;
            }
        }
    } catch (e) {
        windowService.logError("60E76FBB6C1F1FE", e.message);
        return [];
    }

    return items;
};

export const mapFandecksToObjects = (list) => {
    let fandecks = [];

    try {
        for (let fandeck of list) {
            try {
                fandecks.push(
                    new Fandeck(fandeck.id, fandeck.name, fandeck.displayName, fandeck.isLocal, fandeck.enabled)
                );
            } catch (e) {
                windowService.logError("B64C1E87B990D05", e.message);
            }
        }
    } catch (e) {
        windowService.logError("AC64AE950C5BD3C", e.message);
        return [];
    }

    return fandecks;
};

const mapFandecksInGroupsToObjects = (list) => {
    let groups = [];

    try {
        let node, group, fandeck;
        for (node of list.nodes) {
            try {
                group = buildNode(node.id, node.displayName, node.parentId, mapFandecksInGroupsToObjects(node));

                if (node.items !== undefined) {
                    for (fandeck of node.items) {
                        try {
                            group.addItem(
                                new Fandeck(
                                    fandeck.id,
                                    fandeck.name,
                                    fandeck.displayName,
                                    fandeck.isLocal,
                                    fandeck.enabled
                                )
                            );
                        } catch (e) {
                            windowService.logError("93CAA22648317ED", e.message);
                        }
                    }
                }

                groups.push(group);
            } catch (e) {
                windowService.logError("4F931DB6fA5C2EE", e.message);
            }
        }
    } catch (e) {
        windowService.logError("9DD2D35304179AB", e.message);
        return [];
    }

    return groups;
};

export const mapFandecksWithGroupsToObjects = (list) => {
    let groups = [];

    if (list.items.length > 0) {
        let group = buildNode(0, "");

        for (let fandeck of list.items) {
            try {
                group.addItem(
                    new Fandeck(fandeck.id, fandeck.name, fandeck.displayName, fandeck.isLocal, fandeck.enabled)
                );
            } catch (e) {
                windowService.logError("D8D28CCC625E8EC", e.message);
            }
        }

        if (group.items.length || group.nodes.length) {
            groups.push(group);
        }
    }

    groups = groups.concat(mapFandecksInGroupsToObjects(list));

    return groups;
};

export const mapFandecksWithoutGroupsToObjects = (list) => {
    let group = buildNode(0, "");

    try {
        for (let fandeck of list) {
            try {
                group.addItem(
                    new Fandeck(fandeck.id, fandeck.name, fandeck.displayName, fandeck.isLocal, fandeck.enabled)
                );
            } catch (e) {
                windowService.logError("6B382DB44f5fB0D", e.message);
            }
        }
    } catch (e) {
        windowService.logError("7D71DC7599892DF", e.message);
        return [];
    }

    return [group];
};

export const mapMarginToObject = (margin) => {
    if (margin === null) {
        return null;
    }

    try {
        return new Margin(
            margin.id,
            margin.name,
            margin.visible,
            margin.local,
            margin.marginTypeId,
            margin.procProducts ? margin.procProducts : 0,
            margin.procColorants ? margin.procColorants : 0
        );
    } catch (e) {
        windowService.logError("6BD2DB3E1D313ED", e.message);
        return null;
    }
};

export const mapMarginsToObjects = (list) => {
    let margins = [];

    try {
        let margin;
        for (margin of list) {
            try {
                margins.push(
                    new Margin(
                        margin.id,
                        margin.name,
                        margin.visible,
                        margin.local,
                        margin.marginTypeId,
                        margin.procProducts ? margin.procProducts : 0,
                        margin.procColorants ? margin.procColorants : 0
                    )
                );
            } catch (e) {
                windowService.logError("27F08EBFA992380", e.message);
            }
        }
    } catch (e) {
        windowService.logError("389881495492A17", e.message);
        return [];
    }

    return margins;
};

export const mapAllMarginsToObjects = (list) => {
    let margins = [];

    try {
        let margin;

        if (list.default !== undefined && Array.isArray(list.default)) {
            for (margin of list.default) {
                try {
                    margins.push(new Margin(margin.id, margin.name, margin.visible, margin.local, margin.marginTypeId));
                } catch (e) {
                    windowService.logError("05129106197F59E", e.message);
                }
            }
        }

        if (list.custom !== undefined && Array.isArray(list.custom)) {
            for (margin of list.custom) {
                try {
                    margins.push(new Margin(margin.id, margin.name, margin.visible, margin.local, margin.marginTypeId));
                } catch (e) {
                    windowService.logError("2A0EA3705A45FFD", e.message);
                }
            }
        }
    } catch (e) {
        windowService.logError("5BAD5BC6B83934B", e.message);
        return [];
    }

    return margins;
};

export const mapCurrencyDataToObject = (id, name, code, symbol, rate, rounding, visible, local, primary) => {
    try {
        return new Currency(id, name, code, symbol, rate, rounding, local ? visible : true, local, primary);
    } catch (e) {
        windowService.logError("219F93E5C6A3653", e.message);
        return null;
    }
};

export const mapDefaultCurrencyDataToObject = () => {
    try {
        return new Currency(0, "Euro", settings.DEFAULT_CURRENCY_CODE, "â¬", 1, 0, false, true, false);
    } catch (e) {
        windowService.logError("CF98E39A2201732", e.message);
        return null;
    }
};

export const mapCurrencyToObject = (currencyData) => {
    if (currencyData === null || !validationHelper.isCurrencyCodeValid(currencyData.code)) {
        return mapDefaultCurrencyDataToObject();
    }

    try {
        let currency = mapCurrencyDataToObject(
            currencyData.id,
            currencyData.name,
            currencyData.code,
            currencyData.symbol,
            currencyData.rate,
            currencyData.rounding,
            true,
            currencyData.local,
            currencyData.primary
        );

        if (currency === null) {
            return mapDefaultCurrencyDataToObject();
        }

        return currency;
    } catch (e) {
        windowService.logError("072DF1518463673", e.message);
        return mapDefaultCurrencyDataToObject();
    }
};

export const mapCurrenciesToObjects = (list) => {
    let currencies = [];

    try {
        for (let currency of list) {
            try {
                currencies.push(
                    mapCurrencyDataToObject(
                        currency.id,
                        currency.name,
                        currency.code,
                        currency.symbol,
                        currency.rate,
                        currency.rounding,
                        currency.visible,
                        currency.local,
                        currency.primary
                    )
                );
            } catch (e) {
                windowService.logError("CC98308C415CCF6", e.message);
            }
        }
    } catch (e) {
        windowService.logError("8F52A89A04EDD8B", e.message);
        return [];
    }

    return currencies;
};

export const mapUserToObject = (item) => {
    try {
        return new User(
            item.id,
            item.username,
            item.barcode === null ? "" : item.barcode,
            item.local,
            item.deleted,
            item.canDelete,
            item.canEdit,
            item.autoLogin
        );
    } catch (e) {
        windowService.logError("50130E096E01EE2", e.message);
        return null;
    }
};

export const mapUsersToObjects = (list) => {
    let users = [];

    try {
        let user;
        for (user of list) {
            try {
                users.push(
                    new User(
                        user.id,
                        user.username,
                        user.barcode === null ? "" : user.barcode,
                        user.local,
                        user.deleted,
                        user.canDelete,
                        user.canEdit,
                        user.autoLogin
                    )
                );
            } catch (e) {
                windowService.logError("62A01B4DC8AEF7D", e.message);
            }
        }
    } catch (e) {
        windowService.logError("5D000EE17C4A93E", e.message);
        return [];
    }

    return users;
};

export const mapUserGroupsToObjects = (list) => {
    let userGroups = [];

    try {
        let group;
        for (group of list) {
            try {
                userGroups.push(
                    new UserGroup(
                        group.id,
                        group.translation,
                        group.name,
                        group.priority,
                        group.deleted,
                        group.translate,
                        group.default
                    )
                );
            } catch (e) {
                windowService.logError("53C2A9AE4564407", e.message);
            }
        }
    } catch (e) {
        windowService.logError("64C595BD90959ED", e.message);
        return [];
    }

    return userGroups;
};

const findUser = (id, isLocal, list) => {
    let user;

    for (user of list) {
        if (user.id === id) {
            if (user.local === isLocal) {
                return user;
            }
        }
    }
    return null;
};

export const updateUserList = (userId, isLocal, userList, userGroupsList) => {
    let newUser = findUser(userId, isLocal, userList).clone();
    newUser.groups = mapUserGroupsToObjects(userGroupsList);
    let user,
        newUserList = [];
    for (user of userList) {
        if (user.id === userId && user.local === isLocal) {
            newUserList.push(newUser);
        }
        if (user.id !== userId || user.local !== isLocal) {
            newUserList.push(user);
        }
    }
    return newUserList;
};

export const mapCustomersToObjects = (list) => {
    let customers = [];

    try {
        let customer;
        for (customer of list) {
            try {
                customers.push(new Customer(customer.id, customer.name));
            } catch (e) {
                windowService.logError("39C1EF2B67F913F", e.message);
            }
        }
    } catch (e) {
        windowService.logError("DA3380181E0E8C9", e.message);
        return [];
    }

    return customers;
};

export const mapCustomerToObject = (item) => {
    if (item === null || item.id === undefined || item.name === undefined) {
        return null;
    }

    let customer = null;

    try {
        customer = new Customer(item.id, item.name);

        if (item.data !== null && item.data !== undefined) {
            customer.phone = item.data.phone;
            customer.email = item.data.email;
            customer.street = item.data.street;
            customer.city = item.data.city;
            customer.country = item.data.country;
            customer.zipCode = item.data.zipCode;
        }

        if (item.margin !== null && item.margin !== undefined) {
            customer.marginId = item.margin.id;
            customer.marginName = item.margin.name;
        }

        if (item.barcode !== null && item.barcode !== undefined) {
            customer.barcode = item.barcode;
        }

        if (item.expirationDate !== null && item.expirationDate !== undefined) {
            customer.expirationDate = item.expirationDate;
        }

        if (item.identification !== null && item.identification !== undefined) {
            customer.identification = item.identification;
        }
    } catch (e) {
        windowService.logError("5F213078593B4CD", e.message);
        return [];
    }

    return customer;
};

export const mapUfiCodeToString = (item) => {
    if (item === null || item.ufiCode === undefined) {
        return null;
    }

    return item.ufiCode;
};

export const mapColorantBatchNumberToObject = (item) => {
    if (item === null || item.batchNumber === undefined) {
        return null;
    }

    let colorantBatchNumber = null;

    try {
        let colorant = mapColorantToObject(item.item);
        if (!colorant) {
            return null;
        }
        colorantBatchNumber = new ColorantBatchNumber(
            item.id,
            colorant,
            item.batchNumber,
            item.productionDate,
            item.moreInfo,
            item.statusId,
            item.lastStatusChange
        );
    } catch (e) {
        windowService.logError("2CB9B470D4B65C9", e.message);
        return null;
    }

    return colorantBatchNumber;
};

export const mapBatchNumbersToObjects = (batchNumbers) => {
    const result = {};

    let batchNumberObject;

    for (const itemId of Object.keys(batchNumbers)) {
        result[parseInt(itemId)] = [];

        for (const batchNumber of batchNumbers[itemId]) {
            switch (batchNumber.itemTypeId) {
                case itemTypes.COLORANT:
                    batchNumberObject = mapColorantBatchNumberToObject(batchNumber);
                    break;
                default:
                    continue;
            }

            if (batchNumberObject) {
                result[parseInt(itemId)].push(batchNumberObject);
                batchNumberObject = null;
            }
        }
    }

    return result;
};

export const mapPosToObject = (item) => {
    let pos = null;

    try {
        pos = new PosForm(item.name);
        pos.url = item.url;
        pos.zipCode = item.zipCode;
        pos.address = item.address;
        pos.email = item.email;
        pos.secondAddress = item.address2;
        pos.phone = item.phone;
        pos.city = item.city;
        pos.contactName = item.contact;
        pos.shopIdentification = item.identification;
        pos.machineIdentification = item.machineIdentification;
    } catch (e) {
        windowService.logError("008EB44FC720C8B", e.message);
        return new PosForm(null);
    }

    return pos;
};

export const mapUnitCodeToConstant = (unitCode) => {
    switch (unitCode.toLowerCase()) {
        case "g":
            return settings.UNIT_G;
        case "ml":
            return settings.UNIT_ML;
        case "kg":
            return settings.UNIT_KG;
        case "l":
            return settings.UNIT_L;
        default:
            return null;
    }
};

export const mapUnitsToObjects = (list) => {
    let units = [];
    let unitCodes = [];

    try {
        let unit, unitCode;
        for (unit of list) {
            try {
                unitCode = mapUnitCodeToConstant(unit.name);

                if (unitCode !== null && !unitCodes.includes(unitCode)) {
                    unitCodes.push(unitCode);
                    units.push(new Unit(unit.id, unit.name, unitCode, unit.isGravimetric));
                }
            } catch (e) {
                windowService.logError("FB070B0F6BF6096", e.message);
            }
        }
    } catch (e) {
        windowService.logError("6B89BDE463FEE47", e.message);
        return [];
    }

    return units;
};

export const mapTintometricSystemToObject = (item) => {
    if (item === null) {
        return null;
    }

    try {
        return new TintometricSystem(item.id, item.name, item.code);
    } catch (e) {
        windowService.logError("8E763773F67C417", e.message);
        return null;
    }
};

export const mapTintometricSystemToObjects = (list) => {
    let tintometricSystems = [];

    try {
        let ts = null;

        for (let item of list) {
            ts = mapTintometricSystemToObject(item);

            if (ts !== null) {
                tintometricSystems.push(ts);
            }
        }

        return tintometricSystems;
    } catch (e) {
        windowService.logError("E02BBF40A4FB2DB", e.message);
        return [];
    }
};

export const mapBasesToObjects = (list) => {
    let bases = [];

    try {
        for (let base of list) {
            try {
                bases.push(new Base(base.id, base.name, base.displayName, base.density));
            } catch (e) {
                windowService.logError("9C9A1C084D32258", e.message);
            }
        }
    } catch (e) {
        windowService.logError("B4393114EB29B2D", e.message);
        return [];
    }

    return bases;
};

export const mapMatchingColorantsToObject = (list) => {
    let colorants = [];

    try {
        for (let colorant of list) {
            try {
                if (colorant.id !== null) {
                    if (colorant.detail !== null) {
                        colorants.push(
                            new Colorant(
                                colorant.id,
                                colorant.name,
                                colorant.detail.displayName,
                                colorant.detail.rgb,
                                0,
                                settings.UNIT_ML,
                                colorant.detail.density === null ? 1 : colorant.detail.density,
                                colorant.detail.ufiCode
                            )
                        );
                    } else {
                        colorants.push(new Colorant(colorant.id, colorant.name, colorant.name, null, 0));
                    }
                }
            } catch (e) {
                windowService.logError("AC83A621CFE647B", e.message);
            }
        }
    } catch (e) {
        windowService.logError("1B6097C09BCB556", e.message);
        return [];
    }

    return colorants;
};

export const mapMatchingBasesToObjects = (list) => {
    let bases = [];

    try {
        let newBase = null;
        for (let bip of list) {
            try {
                if (bip.detail !== null && bip.detail.baseId !== null) {
                    newBase = new MatchingBase(
                        bip.detail.baseId,
                        bip.name,
                        bip.detail.baseDisplayName,
                        bip.detail.density,
                        bip.id,
                        mapMatchingColorantsToObject(bip.colorants)
                    );

                    newBase.baseInProductId = bip.id;

                    bases.push(newBase);
                }
            } catch (e) {
                windowService.logError("48A6F749D6BACB1", e.message);
            }
        }
    } catch (e) {
        windowService.logError("09BE7833D014594", e.message);
        return [];
    }

    return bases;
};

export const mapBippToObjects = (list) => {
    let bippList = [];
    let newBipp = null;

    try {
        for (let bipp of list) {
            try {
                newBipp = mapBaseInProductPackageToObject(bipp);

                if (newBipp !== null) {
                    bippList.push(newBipp);
                }
            } catch (e) {
                windowService.logError("9721DDA16C675C4", e.message);
            }
        }
    } catch (e) {
        windowService.logError("D033997D901E6F2", e.message);
        return [];
    }

    return bippList;
};

export const mapLoggedUserToObject = (item) => {
    try {
        return new LoggedUser(
            item.id,
            item.username,
            item.barcode,
            item.local,
            item.deleted,
            item.canDelete,
            item.canEdit,
            item.autoLogin,
            mapUserGroupsToObjects(item.groups)
        );
    } catch (e) {
        windowService.logError("CEEC5233B3DEE68", e.message);
        return null;
    }
};

export const mapColorsToObjects = (list, local) => {
    let colors = [];

    try {
        let item;
        for (item of list) {
            try {
                colors.push(
                    mapColorDataToObject(
                        item.id,
                        item.name,
                        item.displayName,
                        item.code,
                        item.rgb,
                        local,
                        true,
                        item.rgbOverride
                    )
                );
            } catch (e) {
                windowService.logError("F8D2B5AA662E012", e.message);
            }
        }
    } catch (e) {
        windowService.logError("5BEAA7BE0ACBAEA", e.message);
        return [];
    }

    return colors;
};

export const mapBrowseColorsToObjects = (list, local) => {
    let colors = [];

    try {
        for (let item of list) {
            try {
                colors.push(
                    mapPaletteColorDataToObject(
                        item.colorId,
                        item.color.name,
                        item.color.displayName,
                        item.code === null ? item.color.code : item.code,
                        item.rgb,
                        local,
                        item.hasFormula,
                        item.id,
                        item.fandeck.id,
                        mapPaletteColorMoreInfoToObjects(item),
                        item.hasImage === true,
                        item.hasReflData
                    )
                );
            } catch (e) {
                windowService.logError("08CEB29AFF8ABBB", e.message);
            }
        }
    } catch (e) {
        windowService.logError("E43406C45677CF7", e.message);
        return [];
    }

    return colors;
};

export const mapClosestColorsToObjects = (list) => {
    let colors = [];

    try {
        let item, color, fandeck;
        for (item of list) {
            try {
                color = mapPaletteColorDataToObject(
                    item.color.id,
                    item.color.name,
                    item.color.displayName,
                    item.color.code,
                    item.color.rgb,
                    item.color.isLocal,
                    item.hasFormula,
                    item.id,
                    item.fandeckId
                );
                fandeck = mapFandeckToObject(item.fandeck);

                if (color !== null && fandeck !== null) {
                    colors.push({
                        color: color,
                        fandeck: fandeck,
                        delta: item.cieDe2000,
                        priceList: []
                    });
                }
            } catch (e) {
                windowService.logError("A1954352D764E9A", e.message);
            }
        }
    } catch (e) {
        windowService.logError("62E428B247E8AE4", e.message);
        return [];
    }

    return colors;
};

export const mapClosestColorsWithPricesToObjects = (list) => {
    let colors = [];

    try {
        let color, fandeck;

        for (let item of list) {
            try {
                color = mapColorDataToObject(
                    item.formula.color.id,
                    item.formula.color.name,
                    item.formula.color.displayName,
                    item.formula.color.code,
                    item.formula.color.rgb,
                    item.formula.color.isLocal
                );
                fandeck = mapFandeckToObject(item.formula.fandeck);

                if (color !== null && fandeck !== null) {
                    colors.push({
                        fandeck: fandeck,
                        color: color,
                        delta: item.cieDe2000,
                        priceList: mapFormulaPricesToObjects(item.priceData)
                    });
                }
            } catch (e) {
                windowService.logError("1B75EBC342FC176", e.message);
            }
        }
    } catch (e) {
        windowService.logError("C78A053035AD6F7", e.message);
        return [];
    }

    return colors;
};

export const sortColorantList = (colorantList, orderBy) => {
    if (orderBy === settings.ORDER_BY_NAME) {
        return colorantList.sort((a, b) => a.name.localeCompare(b.name));
    }

    if (orderBy === settings.ORDER_BY_AMOUNT) {
        return colorantList.sort((a, b) => b.amount - a.amount);
    }

    return colorantList;
};

export const sortCorrectionList = (colorantList, orderBy) => {
    if (orderBy === settings.ORDER_BY_NAME) {
        return colorantList.sort((a, b) => a.name.localeCompare(b.name));
    }

    if (orderBy === settings.ORDER_BY_AMOUNT) {
        return colorantList.sort((a, b) => b.correction - a.correction);
    }

    return colorantList;
};

export const getColorantListFromFormula = (colorantAmounts, allColorantList, unit, orderBy = null) => {
    let colorantList = [];

    try {
        let colorant;
        let newColorant;

        let colorantName;
        for (colorantName in colorantAmounts) {
            try {
                colorant = getterHelper.getObjectByName(allColorantList, colorantName);

                if (colorant !== null) {
                    newColorant = colorant.clone();
                    newColorant.amount = formatHelper.formatFormulaDecimalNumber(colorantAmounts[colorantName]);
                    newColorant.realAmount = colorantAmounts[colorantName];
                    newColorant.amountType = unit;

                    colorantList.push(newColorant);
                }
            } catch (e) {
                windowService.logError("568B5347FED8773", e.message);
            }
        }
    } catch (e) {
        windowService.logError("727A5B6963FF51D", e.message);
        return colorantList;
    }

    return sortColorantList(colorantList, orderBy);
};

export const getDispensedColorantListFromFormula = (colorantAmounts, allColorantList, unit, orderBy = null) => {
    let colorantList = [];

    try {
        let colorant;
        let newColorant;

        let colorantName;
        for (colorantName in colorantAmounts) {
            try {
                colorant = getterHelper.getObjectByName(allColorantList, colorantName);

                if (colorant !== null) {
                    newColorant = colorant.clone();
                    newColorant.amount = formatHelper.formatFormulaDecimalNumber(colorantAmounts[colorantName]);
                    newColorant.realAmount = colorantAmounts[colorantName];
                    newColorant.amountType = unit;
                } else {
                    newColorant = new MachineColorant(
                        0,
                        colorantName,
                        colorantName,
                        config.DEFAULT_RGB,
                        colorantAmounts[colorantName],
                        unit
                    );
                }

                colorantList.push(newColorant);
            } catch (e) {
                windowService.logError("64A8932761BBADD", e.message);
            }
        }
    } catch (e) {
        windowService.logError("469AB83B38EC827", e.message);
        return colorantList;
    }

    return sortColorantList(colorantList, orderBy);
};

export const buildCalculatedFormulaObject = (
    color,
    local,
    formula,
    shotsName,
    allColorantList,
    colorantPanelOrderBy,
    formulaCombinationId
) => {
    let newFormula = null;

    try {
        newFormula = mapFormulaToObject(
            formula.id,
            color.id,
            color.name,
            color.displayName,
            color.code,
            color.rgb,
            local
        );
    } catch (e) {
        windowService.logError("254907BBFAD3BEF", e.message);
        newFormula = mapFormulaToObject(0, 0, "undefined", "undefined", "undefined", config.DEFAULT_RGB, local);
    }

    newFormula.formulaCombinationId = formulaCombinationId;
    newFormula.created = new Date(formula.created);
    newFormula.updated = new Date(formula.updated);
    newFormula.enabled = formula.enabled;
    newFormula.verified = formula.verified;
    newFormula.rejected = formula.rejected;
    newFormula.notAvailable = formula.notAvailable;
    newFormula.colorantList = getColorantListFromFormula(
        formula.colorantAmountsByUnits.ml,
        allColorantList,
        settings.UNIT_ML,
        colorantPanelOrderBy
    );
    newFormula.colorantListG = getColorantListFromFormula(
        formula.colorantAmountsByUnits.g,
        allColorantList,
        settings.UNIT_G,
        colorantPanelOrderBy
    );
    newFormula.colorantListShot = getColorantListFromFormula(
        formula.colorantAmountsByUnits.shots,
        allColorantList,
        shotsName,
        colorantPanelOrderBy
    );
    newFormula.colorantsGroupId = formula.colorantsGroupId;
    newFormula.destinationId = formula.destinationId;
    newFormula.formulaSourceId = formula.formulaSourceId;

    return newFormula;
};

export const buildFormulaObject = (
    id,
    color,
    rgb,
    originalFormula,
    dispensedFormula,
    shotsName,
    allColorantList,
    scaling,
    colorantPanelOrderBy
) => {
    let newFormula = null;

    try {
        newFormula = mapFormulaToObject(
            id,
            color.id === null ? config.UNKNOWN_ID : color.id,
            color.name,
            color.displayName,
            color.code,
            rgb,
            color.isLocal
        );
    } catch (e) {
        windowService.logError("2618BF7F2E7BADF", e.message);
        return null;
    }

    try {
        newFormula.colorantList = getColorantListFromFormula(
            originalFormula.ml,
            allColorantList,
            settings.UNIT_ML,
            colorantPanelOrderBy
        );
        newFormula.colorantListG = getColorantListFromFormula(
            originalFormula.g,
            allColorantList,
            settings.UNIT_G,
            colorantPanelOrderBy
        );
        newFormula.colorantListShot = getColorantListFromFormula(
            originalFormula.shots,
            allColorantList,
            shotsName,
            colorantPanelOrderBy
        );
        newFormula.scale(scaling);
    } catch (e) {
        windowService.logError("29EC19F57ED72B8", e.message);
        return null;
    }

    try {
        newFormula.dispensedColorantList = getDispensedColorantListFromFormula(
            dispensedFormula.ml,
            allColorantList,
            settings.UNIT_ML,
            colorantPanelOrderBy
        );
        newFormula.dispensedColorantListG = getDispensedColorantListFromFormula(
            dispensedFormula.g,
            allColorantList,
            settings.UNIT_G,
            colorantPanelOrderBy
        );
        newFormula.dispensedColorantListShot = getDispensedColorantListFromFormula(
            dispensedFormula.shots,
            allColorantList,
            shotsName,
            colorantPanelOrderBy
        );
    } catch (e) {
        windowService.logError("FFF6BF49A93CD86", e.message);
        return null;
    }

    return newFormula;
};

export const mapBaseToObject = (base, baseInProduct = null) => {
    if (base === null || base.id === undefined || base.name === undefined) {
        return null;
    }

    let newBase = null;

    try {
        newBase = new Base(base.id, base.name, base.displayName, base.density === undefined ? 0 : base.density);

        if (baseInProduct !== undefined && baseInProduct !== null && baseInProduct.id !== null) {
            newBase.baseInProductId = baseInProduct.id;
            newBase.baseInProductFillProc =
                baseInProduct.fillProc === null || baseInProduct.fillProc === 0 ? 1 : baseInProduct.fillProc;
            newBase.baseInProductUfi = baseInProduct.ufiCode;

            if (baseInProduct.density !== 0) {
                newBase.density = baseInProduct.density;
            }
        }
    } catch (e) {
        windowService.logError("E73CCF9F423D7B5", e.message);
        return null;
    }

    return newBase;
};

export const mapFormulaPricesToObjects = (list) => {
    if (!Array.isArray(list)) {
        return [];
    }

    let priceList = [];

    try {
        for (let item of list) {
            if (
                item.id === priceTypes.BASE_SELL_FULL ||
                item.id === priceTypes.CNT_SELL_FULL ||
                item.id === priceTypes.CNT_SELL_FULL_BY_NAME ||
                item.id === priceTypes.TINT_SELL_FULL_ALL ||
                item.id === priceTypes.TINT_SELL_FULL_PER_PKG ||
                item.id === priceTypes.TOTAL_SELL_FULL_ALL ||
                item.id === priceTypes.TOTAL_SELL_FULL_PER_L ||
                item.id === priceTypes.TOTAL_SELL_FULL_PER_KG ||
                item.id === priceTypes.TOTAL_SELL_FULL_PER_PKG
            ) {
                continue;
            }

            switch (item.type) {
                case "price":
                    try {
                        priceList.push(
                            new Price(
                                item.id,
                                item.price,
                                item.currency,
                                item.currencySymbol,
                                translationHelper.getPriceTypesTranslation(item.id),
                                item.withVat > 0,
                                item.withVat,
                                item.visible
                            )
                        );
                    } catch (e) {
                        windowService.logError("DEE9EFBEB55F768", e.message);
                    }
                    break;
                case "prices":
                    try {
                        priceList.push(
                            new PriceColorants(
                                item.id,
                                item.prices === undefined ? {} : item.prices,
                                item.currency === undefined ? "ZITA" : item.currency,
                                item.currencySymbol === undefined ? "ZK" : item.currencySymbol,
                                translationHelper.getPriceTypesTranslation(item.id),
                                item.pricesWithVat === undefined ? false : Object.keys(item.pricesWithVat).length > 0,
                                item.pricesWithVat === undefined ? {} : item.pricesWithVat,
                                item.visible
                            )
                        );
                    } catch (e) {
                        windowService.logError("AD35CCFAE2EDD13", e.message);
                    }
                    break;
                case "number":
                    try {
                        priceList.push(
                            new PriceGroup(
                                item.id,
                                item.value,
                                translationHelper.getPriceTypesTranslation(item.id),
                                item.visible
                            )
                        );
                    } catch (e) {
                        windowService.logError("743773C20CF8769", e.message);
                    }
                    break;
                case "string":
                    try {
                        priceList.push(
                            new PriceString(
                                item.id,
                                item.value,
                                translationHelper.getPriceTypesTranslation(item.id),
                                item.visible
                            )
                        );
                    } catch (e) {
                        windowService.logError("0C0AFF87B770C94", e.message);
                    }
                    break;
                default:
                    break;
            }
        }
    } catch (e) {
        windowService.logError("5D5D82E34030919", e.message);
        return [];
    }

    return priceList;
};

export const formatPricesForStats = (priceList) => {
    let result = [];

    let price;
    for (price of priceList) {
        if (price instanceof Price) {
            result.push({
                id: price.id,
                price: price.price,
                currency: price.currency,
                currencySymbol: price.currencySymbol,
                withVat: price.vat,
                type: "price",
                visible: price.visible
            });
        } else if (price instanceof PriceColorants) {
            result.push({
                id: price.id,
                prices: price.prices,
                pricesWithVat: price.vats,
                type: "prices",
                visible: price.visible
            });
        } else if (price instanceof PriceGroup) {
            result.push({
                id: price.id,
                type: "number",
                value: price.value,
                visible: price.visible
            });
        } else if (price instanceof PriceString) {
            result.push({
                id: price.id,
                type: "string",
                value: price.value,
                visible: price.visible
            });
        }
    }

    return result;
};

export const mapFilesToObjects = (list) => {
    if (!Array.isArray(list)) {
        return [];
    }

    let fileList = [];

    try {
        for (let item of list) {
            try {
                fileList.push(
                    new ProductFile(
                        item.id,
                        item.name,
                        item.path,
                        item.mimeType,
                        item.fileTypeId,
                        item.moreInfo,
                        item.url,
                        0,
                        0,
                        item.uploaded
                    )
                );
            } catch (e) {
                windowService.logError("F36DF35159FF516", e.message);
            }
        }
    } catch (e) {
        windowService.logError("1731370A2703ADC", e.message);
        return [];
    }

    return fileList;
};

export const mapServersToObject = (list) => {
    let servers = [];

    try {
        let newServer, server;
        for (server of list) {
            try {
                newServer = new Server(server.ip, server.port, server.data.version, server.data.name);
                newServer.enabled = server.enabled;
                servers.push(newServer);
            } catch (e) {
                windowService.logError("48719E5985D4833", e.message);
            }
        }
    } catch (e) {
        windowService.logError("EC192FEAC95ED5C", e.message);
        return [];
    }

    return servers;
};

export const mapAvailableServersToObject = (list) => {
    let servers = [];

    try {
        let server, item;
        for (item of list) {
            try {
                server = new Server(item.host, item.port, item.version, item.name);
                server.id = item.id;
                server.lastActivity = item.lastActivity;
                server.active = item.active;
                server.uuid = item.uuid;
                server.note = item.note;
                server.enabled = item.enabled;
                servers.push(server);
            } catch (e) {
                windowService.logError("163C0DAB8190129", e.message);
            }
        }
    } catch (e) {
        windowService.logError("82107F5672408E5", e.message);
        return [];
    }

    return servers;
};

const mapMainDatabaseObject = (database) => {
    if (database === undefined || database === null || database.mainVersion === undefined) {
        return new MainDatabase(0, 0, "0.0.0.0", "", "");
    }

    try {
        return new MainDatabase(
            database.mainVersion.id,
            database.mainVersion.minId,
            database.mainVersion.version,
            database.mainVersion.customerVersionId,
            database.mainVersion.customerVersion,
            database.mainVersion.creationDate,
            database.mainVersion.installationDate,
            database.mainVersion.shotsCoef,
            database.mainVersion.moreInfo,
            database.mainVersion.changelog
        );
    } catch (e) {
        windowService.logError("46A8B3EACEB56A1", e.message);
        return new MainDatabase(0, 0, "0.0.0.0", "", "");
    }
};

export const mapDatabaseToObject = (database) => {
    let mainDatabase = mapMainDatabaseObject(database);

    if (
        database === undefined ||
        database === null ||
        database.uuid === undefined ||
        database.name === undefined ||
        database.localVersion === undefined
    ) {
        try {
            return new Database("0", "0", "R2-D2", mainDatabase, {id: 0, minId: 0, version: "0.0.0.0"}, false, "", 0);
        } catch (e) {
            windowService.logError("924E661E912A4C6", e.message);
        }
    }

    return new Database(
        database.uuid,
        database.masterUuid,
        database.name === null ? "Unknown" : database.name,
        mainDatabase,
        database.localVersion,
        database.hasAvailableCompatibleServers,
        database.note,
        database.status
    );
};

export const mapAllDatabaseList = (list) => {
    let databases = [];

    try {
        let db;
        for (db of list) {
            try {
                databases.push(
                    new Database(
                        db.uuid,
                        db.masterUuid,
                        db.name === null ? "Unknown" : db.name,
                        null,
                        null,
                        false,
                        "",
                        db.status
                    )
                );
            } catch (e) {
                windowService.logError("417AD02B721A929", e.message);
            }
        }
    } catch (e) {
        windowService.logError("0AB82C815F3C37C", e.message);
        return [];
    }

    return databases;
};

export const mapLicenseContactPerson = (registrationData) => {
    if (typeof registrationData.ContactPerson !== "object" || registrationData.ContactPerson === null) {
        return new LicenseContactPerson();
    }

    try {
        return new LicenseContactPerson(
            registrationData.ContactPerson.Name ?? "",
            registrationData.ContactPerson.Email ?? "",
            registrationData.ContactPerson.Phone ?? ""
        );
    } catch (e) {
        windowService.logError("45D861EC0C9ABA1", e.message);
        return new LicenseContactPerson();
    }
};

export const mapLicensePointOfSale = (registrationData) => {
    if (typeof registrationData.PointOfSale !== "object" || registrationData.PointOfSale === null) {
        return new LicensePointOfSale();
    }

    try {
        return new LicensePointOfSale(
            registrationData.PointOfSale.Name ?? "",
            registrationData.PointOfSale.Street ?? "",
            registrationData.PointOfSale.Country ?? "",
            registrationData.PointOfSale.Region ?? "",
            registrationData.PointOfSale.City ?? "",
            registrationData.PointOfSale.Zip ?? ""
        );
    } catch (e) {
        windowService.logError("64F0B80C6156E05", e.message);
        return new LicensePointOfSale();
    }
};

export const mapLicenseMachineInformation = (registrationData) => {
    if (typeof registrationData.Machine !== "object" || registrationData.Machine === null) {
        return new LicenseMachineInformation();
    }

    try {
        return new LicenseMachineInformation(
            registrationData.Machine.SerialNumber ?? "",
            registrationData.Machine.Producer ?? "",
            registrationData.Machine.Type ?? "",
            registrationData.Machine.NumberOfCanisters ?? ""
        );
    } catch (e) {
        windowService.logError("A4B93C9CE69DB68", e.message);
        return new LicenseMachineInformation();
    }
};

export const getEmptyRegistrationObject = () => {
    return new RegistrationForm(new LicenseContactPerson(), new LicensePointOfSale(), new LicenseMachineInformation());
};

export const mapRegistrationDataToObject = (registrationData) => {
    if (typeof registrationData !== "object" || registrationData === null) {
        return getEmptyRegistrationObject();
    }

    let contactPerson, pointOfSale, machineInformation;

    try {
        contactPerson = mapLicenseContactPerson(registrationData);
        pointOfSale = mapLicensePointOfSale(registrationData);
        machineInformation = mapLicenseMachineInformation(registrationData);
    } catch (e) {
        windowService.logError("09A06796FDC6336", e.message);
        contactPerson = new LicenseContactPerson();
        pointOfSale = new LicensePointOfSale();
        machineInformation = new LicenseMachineInformation();
    } finally {
        return new RegistrationForm(contactPerson, pointOfSale, machineInformation);
    }
};

export const mapLicenseProductsToObject = (list) => {
    let productList = [];

    try {
        for (var key in list) {
            try {
                productList.push(new LicenseProduct(Number(key), list[key]));
            } catch (e) {
                windowService.logError("0DBA3FC99C152C5", e.message);
            }
        }
    } catch (e) {
        windowService.logError("B43B81AD7721974", e.message);
        return [];
    }

    return productList;
};

export const mapLicenseProductsToObjectOrdered = (list, order) => {
    if (order.length === 0) {
        return mapLicenseProductsToObject(list);
    }

    let productList = [];

    try {
        for (let key of order) {
            try {
                productList.push(new LicenseProduct(Number(key), list[key]));
            } catch (e) {
                windowService.logError("2717F4EB3E13A1E", e.message);
            }
        }
    } catch (e) {
        windowService.logError("8DE9708A1CDF1E0", e.message);
        return [];
    }

    return productList;
};

export const updateLicenseProducts = (list, counts) => {
    let product,
        key,
        newProduct,
        updated = [];
    for (product of list) {
        for (key in counts) {
            if (product.id === Number(key)) {
                try {
                    newProduct = new LicenseProduct(product.id, product.name);
                    newProduct.availableCount = counts[key].available;
                    newProduct.totalCount = counts[key].total;
                    updated.push(newProduct);
                } catch (e) {
                    windowService.logError("FEC410D307598B3", e.message);
                }
            }
        }
    }
    return updated;
};

export const getServerObjectFromUrl = (url) => {
    let parts = url.split(":");

    return new Server(parts[1].slice(2, parts[1].length), parts[2], null, parts[1].slice(2, parts[1].length));
};

export const mapOrderToObject = (orderData) => {
    try {
        let order = new Order(
            orderData.id,
            orderData.identifier,
            orderData.status,
            orderData.brand,
            orderData.productName,
            orderData.productDisplayName,
            orderData.fandeckName,
            orderData.fandeckDisplayName,
            orderData.colorName,
            orderData.colorDisplayName,
            orderData.colorRgb,
            orderData.baseName,
            orderData.baseDisplayName,
            orderData.packageName,
            orderData.packageDisplayName,
            orderData.bippName,
            orderData.dispensedPackageCount,
            orderData.packageCount,
            orderData.nominalAmount,
            orderData.unit.id,
            orderData.created,
            orderData.customerName,
            orderData.canAcquire,
            orderData.clientName,
            orderData.customPackageName,
            orderData.colorCode
        );

        if (orderData.fandeckAlternativeName !== undefined) {
            order.fandeckAlternativeName = orderData.fandeckAlternativeName;
        }

        if (orderData.productAlternativeName !== undefined) {
            order.productAlternativeName = orderData.productAlternativeName;
        }

        if (orderData.colorAlternativeName !== undefined) {
            order.colorAlternativeName = orderData.colorAlternativeName;
        }

        if (orderData.moreInfo !== undefined) {
            order.note = orderData.moreInfo;
        }

        if (orderData.moreInfoInternal !== undefined) {
            order.moreInfoInternal = orderData.moreInfoInternal;
        }

        return order;
    } catch (e) {
        windowService.logError("F52A0964B352EC9", e.message);
        return null;
    }
};

export const mapOrdersToObjects = (orderList) => {
    let orders = [];

    try {
        let order, orderData;
        for (orderData of orderList) {
            order = mapOrderToObject(orderData);

            if (order !== null) {
                orders.push(order);
            }
        }
    } catch (e) {
        windowService.logError("C563607BA22A167", e.message);
        return [];
    }

    return orders;
};

const getCustomColorantName = (colorantNames, name) => {
    if (colorantNames === null) {
        return name;
    }

    if (colorantNames[name] === undefined) {
        return name;
    }

    return colorantNames[name];
};

const processColorants = (colorants, orderBy) => {
    let customColorantNames = windowService.getCustomColorantNames();
    let orderColorants = [];

    try {
        let colorantName, customColorantName;
        for (colorantName in colorants) {
            try {
                customColorantName = getCustomColorantName(customColorantNames, colorantName);

                orderColorants.push(
                    new Colorant(
                        0,
                        customColorantName,
                        customColorantName,
                        config.DEFAULT_RGB,
                        colorants[colorantName],
                        settings.UNIT_ML
                    )
                );
            } catch (e) {
                windowService.logError("E4DF9CB4575AF9D", e.message);
            }
        }
    } catch (e) {
        windowService.logError("E0C26D5A5356B6D", e.message);
        return [];
    }

    return sortColorantList(orderColorants, orderBy);
};

const processOrderColorantsG = (colorants, orderBy) => {
    let customColorantNames = windowService.getCustomColorantNames();
    let orderColorants = [];

    try {
        let colorantName, customColorantName;
        for (colorantName in colorants) {
            try {
                customColorantName = getCustomColorantName(customColorantNames, colorantName);

                orderColorants.push(
                    new Colorant(
                        0,
                        customColorantName,
                        customColorantName,
                        config.DEFAULT_RGB,
                        colorants[colorantName],
                        settings.UNIT_G
                    )
                );
            } catch (e) {
                windowService.logError("1DE94913E91D60C", e.message);
            }
        }
    } catch (e) {
        windowService.logError("AD9367EF9461020", e.message);
        return [];
    }

    return sortColorantList(orderColorants, orderBy);
};

const processOrderColorantShots = (colorants, shotsName, orderBy) => {
    let usableShotsName = shotsName;
    if (shotsName === undefined || shotsName === null) {
        usableShotsName = settings.UNIT_SHOT;
    }

    let customColorantNames = windowService.getCustomColorantNames();
    let orderColorants = [];

    try {
        let colorantName, customColorantName;
        for (colorantName in colorants) {
            try {
                customColorantName = getCustomColorantName(customColorantNames, colorantName);

                orderColorants.push(
                    new Colorant(
                        0,
                        customColorantName,
                        customColorantName,
                        config.DEFAULT_RGB,
                        colorants[colorantName],
                        usableShotsName
                    )
                );
            } catch (e) {
                windowService.logError("5E5359ADAE1489D", e.message);
            }
        }
    } catch (e) {
        windowService.logError("5691924DBA56339", e.message);
        return [];
    }

    return sortColorantList(orderColorants, orderBy);
};

export const mapPlainOrderDataToObject = (order, database) => {
    let orderData = null;

    try {
        let priceList = mapFormulaPricesToObjects(order.prices);

        let newFormula = mapFormulaToObject(
            null,
            0,
            order.colorName,
            order.colorDisplayName,
            order.colorCode,
            order.colorRgb,
            false
        );
        newFormula.colorantList = processColorants(order.colorants, order.colorantPanelOrderBy);
        newFormula.colorantListG = processOrderColorantsG(order.colorantAmountsByUnits.g, order.colorantPanelOrderBy);
        newFormula.colorantListShot = processOrderColorantShots(
            order.colorantAmountsByUnits.shots,
            order.shotsName,
            order.colorantPanelOrderBy
        );
        newFormula.dispensedColorantList = newFormula.colorantList;
        newFormula.dispensedColorantListG = newFormula.colorantListG;
        newFormula.dispensedColorantListShot = newFormula.colorantListShot;
        newFormula.setScaling(order.scaling === null ? 1 : order.scaling);

        orderData = new OrderData(order.id, order.identifier, order.status);
        orderData.databaseName = order.brand;
        orderData.product = new Product(0, order.productName, order.productDisplayName);
        orderData.fandeck = new Fandeck(0, order.fandeckName, order.fandeckDisplayName);
        orderData.formula = newFormula;
        orderData.base = new Base(0, order.baseName, order.baseDisplayName);
        orderData.package = new Package(
            0,
            order.packageIsCustom
                ? translate("statisticsTab.customPackageName", order.customPackageName)
                : order.packageName,
            order.packageIsCustom
                ? translate("statisticsTab.customPackageName", order.customPackageName)
                : order.packageDisplayName,
            order.unit ? order.unit.id : order.unitId,
            window.storeProvider.getUnitCode(order.unit ? order.unit.id : order.unitId),
            order.nominalAmount,
            order.fillVolume,
            order.fillVolume * 1000,
            order.unit ? order.unit.isGravimetric : order.isGravimetric
        );
        orderData.dispensedPackageCount = order.dispensedPackageCount;
        orderData.packageCount = order.packageCount;
        orderData.created = new Date(order.created);
        orderData.customerName = order.customerName;
        orderData.canAcquire = order.canAcquire;
        orderData.clientName = order.clientName;
        orderData.priceList = priceList;
        orderData.note = order.moreInfo === null ? "" : order.moreInfo;
        orderData.moreInfoInternal = order.moreInfoInternal === null ? "" : order.moreInfoInternal;
        orderData.formulaMoreInfo =
            order.formulaMoreInfoDetailed !== null ? order.formulaMoreInfoDetailed.textFormatted : null;
        orderData.databaseAvailability = componentTypes.ORDER_DATABASE_AVAILABLE_UNKNOWN_STATE;
    } catch (e) {
        windowService.logError("D065CEAABBE9A9E", e.message);
        return null;
    }

    if (orderData.databaseName === database) {
        orderData.databaseAvailability = componentTypes.ORDER_DATABASE_AVAILABLE_OK;
    }

    return orderData;
};

export const mapOrderDataToObject = (order) => {
    let orderData = null;

    try {
        let priceList = mapFormulaPricesToObjects(order.prices);

        let newFormula = mapFormulaToObject(
            null,
            0,
            order.colorName,
            order.colorDisplayName,
            order.colorCode,
            order.colorRgb,
            false
        );
        newFormula.colorantList = processColorants(order.colorants, order.colorantPanelOrderBy);
        newFormula.colorantListG = processOrderColorantsG(order.colorantAmountsByUnits.g, order.colorantPanelOrderBy);
        newFormula.colorantListShot = processOrderColorantShots(
            order.colorantAmountsByUnits.shots,
            order.shotsName,
            order.colorantPanelOrderBy
        );
        newFormula.dispensedColorantList = newFormula.colorantList;
        newFormula.dispensedColorantListG = newFormula.colorantListG;
        newFormula.dispensedColorantListShot = newFormula.colorantListShot;
        newFormula.setScaling(order.scaling === null ? 1 : order.scaling);

        orderData = new OrderData(order.id, order.identifier, order.status);
        orderData.databaseName = order.brand;
        orderData.product = new Product(0, order.productName, order.productDisplayName);
        orderData.fandeck = new Fandeck(0, order.fandeckName, order.fandeckDisplayName);
        orderData.formula = newFormula;
        orderData.base = new Base(0, order.baseName, order.baseDisplayName);
        orderData.package = new Package(
            0,
            order.packageIsCustom
                ? translate("statisticsTab.customPackageName", order.customPackageName)
                : order.packageName,
            order.packageIsCustom
                ? translate("statisticsTab.customPackageName", order.customPackageName)
                : order.packageDisplayName,
            order.unit ? order.unit.id : order.unitId,
            window.storeProvider.getUnitCode(order.unit ? order.unit.id : order.unitId),
            order.nominalAmount,
            order.fillVolume,
            order.fillVolume * 1000,
            order.unit ? order.unit.isGravimetric : order.isGravimetric
        );
        orderData.dispensedPackageCount = order.dispensedPackageCount;
        orderData.packageCount = order.packageCount;
        orderData.created = new Date(order.created);
        orderData.customerName = order.customerName;
        orderData.canAcquire = order.canAcquire;
        orderData.clientName = order.clientName;
        orderData.priceList = priceList;
        orderData.note = order.moreInfo === null ? "" : order.moreInfo;
        orderData.moreInfoInternal = orderData.formulaMoreInfo === null ? "" : order.moreInfoInternal;
        order.formulaMoreInfoDetailed !== null ? order.formulaMoreInfoDetailed.textFormatted : null;
        orderData.databaseAvailability = componentTypes.ORDER_DATABASE_AVAILABLE_OK;
    } catch (e) {
        windowService.logError("0BDFCA2EF480173", e.message);
        return null;
    }

    return orderData;
};

export const mapBarcodeOrderToObject = (item) => {
    try {
        let order = new Order(
            0,
            "",
            1,
            item.brand,
            item.product,
            item.product,
            item.fandeck,
            item.fandeck,
            item.color,
            item.color,
            config.DEFAULT_RGB,
            item.base,
            item.base,
            item.packageName,
            item.packageName,
            item.packageName,
            item.dispensedPackageCount,
            item.packageCount,
            item.nominalAmount,
            item.unit.id,
            null,
            item.customer,
            item.canAcquire,
            item.clientName
        );

        return order;
    } catch (e) {
        windowService.logError("01FDA6367603FC0", e.message);
        return null;
    }
};

export const mapHistoryRecordsToObjects = (refillRecords) => {
    let records = [];

    try {
        let record;
        for (record of refillRecords) {
            try {
                records.push(
                    new RefillHistoryRecord(
                        record.id,
                        record.date,
                        record.index,
                        record.volumeMl,
                        record.amount,
                        record.ufiCode,
                        record.batchNumber,
                        record.actionType
                    )
                );
            } catch (e) {
                windowService.logError("B6699C2FE4C2E80", e.message);
            }
        }
    } catch (e) {
        windowService.logError("422075B47FAC8AC", e.message);
        return [];
    }
    return records;
};

export const mapPrintersToObjects = (printerList) => {
    let printers = [];
    try {
        let printer;
        for (printer of printerList) {
            try {
                printers.push(new Printer(printer.name, printer.description, printer.comment, printer.default));
            } catch (e) {
                windowService.logError("4652A5AD8CC306A", e.message);
            }
        }
    } catch (e) {
        windowService.logError("D5EA3FA89CCC457", e.message);
        return [];
    }
    return printers;
};

export const mapTemplatesToObjects = (templateList) => {
    let templates = [];

    try {
        let template;
        for (template of templateList) {
            try {
                templates.push(new PrinterTemplate(template.name, template.file, template.path));
            } catch (e) {
                windowService.logError("91B3DEE15D80657", e.message);
            }
        }
    } catch (e) {
        windowService.logError("A19DB134814F4D7", e.message);
        return [];
    }

    return templates.sort((a, b) => a.name.localeCompare(b.name));
};

export const mapProductToObject = (data) => {
    if (data === null) {
        return null;
    }

    try {
        return new Product(data.id, data.name, data.displayName, data.code);
    } catch (e) {
        windowService.logError("1F9E577B41D5635", e.message);
        return null;
    }
};

export const mapFandeckToObject = (data) => {
    if (data === null) {
        return null;
    }

    try {
        return new Fandeck(data.id, data.name, data.displayName, data.isLocal);
    } catch (e) {
        windowService.logError("90DBCA84E25D7D4", e.message);
        return null;
    }
};

export const mapCustomFandeckToObject = () => {
    try {
        return new Fandeck(0, null, null, true);
    } catch (e) {
        windowService.logError("B469B81F10F0E32", e.message);
        return null;
    }
};

export const mapColorInFandeckToObject = (data) => {
    try {
        let fandeck = new Fandeck(data.fandeck.id, data.fandeck.name, data.fandeck.displayName);
        let color = mapColorDataToObject(
            data.color.id,
            data.color.name,
            data.color.displayName,
            data.color.code,
            data.color.rgb
        );
        return new ColorInFandeck(data.id, color, fandeck);
    } catch (e) {
        windowService.logError("4EECCD40595C9A4", e.message);
        return null;
    }
};

export const mapCifEntitiesToObject = (id, color, fandeck) => {
    try {
        return new ColorInFandeck(id, color, fandeck);
    } catch (e) {
        windowService.logError("C60F7C6674BF2FF", e.message);
        return null;
    }
};

export const mapCustomPackageToObject = (data, fillingMl) => {
    try {
        let packageName = data.customPackageName === null ? null : data.customPackageName.join(" ");

        return new Package(
            0,
            packageName === null && data.packageName !== null ? data.packageName : packageName,
            packageName === null && data.packageDisplayName !== null ? data.packageDisplayName : packageName,
            data.unit ? data.unit.id : data.unitId,
            window.storeProvider.getUnitCode(data.unit ? data.unit.id : data.unitId),
            data.nominalAmount,
            data.fillVolume,
            fillingMl,
            data.isGravimetric
        );
    } catch (e) {
        windowService.logError("8A98F686AB1A1EF", e.message);
        return null;
    }
};

const getPackageFilling = (nominalAmount, unit, base) => {
    if (base === null) {
        return nominalAmount;
    }

    if (unit === settings.UNIT_L || unit === settings.UNIT_ML || base.density === null || base.density === 0) {
        if (unit === settings.UNIT_ML) {
            return (nominalAmount * base.baseInProductFillProc) / 1000;
        }

        return nominalAmount * base.baseInProductFillProc;
    }

    return (
        conversionHelper.convertByUnits(nominalAmount, unit, settings.UNIT_L, base.density, 1) *
        base.baseInProductFillProc
    );
};

export const buildCustomPackage = (id, size, unit, pack, base) => {
    if (unit === null || pack === null) {
        return null;
    }

    try {
        let packageFilling = getPackageFilling(size, unit.code, base);

        return pack.updatePackage(
            new Package(
                id,
                translate("statisticsTab.customPackageName", ["", size, unit.name]),
                translate("statisticsTab.customPackageName", ["", size, unit.name]),
                unit.id,
                unit.code,
                size,
                packageFilling,
                packageFilling * 1000,
                unit.isGravimetric
            )
        );
    } catch (e) {
        windowService.logError("230B0AB0B79FA09", e.message);
        return null;
    }
};

export const mapSpectroFormulasToObjects = (list) => {
    let formulas = [];

    try {
        let item, formula, items;
        for (formula of list) {
            items = [];
            for (item of formula.items) {
                items.push({
                    product: new Product(
                        item.product.id,
                        item.product.name,
                        item.product.displayName,
                        item.product.code,
                        ""
                    ),
                    delta: item.delta
                });
            }

            try {
                if (items.length > 0) {
                    formulas.push({
                        fandeck: new Fandeck(
                            item.fandeck.id,
                            item.fandeck.name,
                            item.fandeck.displayName,
                            formula.local
                        ),
                        formula: mapColorDataToObject(
                            formula.color.id,
                            formula.color.name,
                            formula.color.displayName,
                            formula.color.code,
                            formula.color.rgb,
                            formula.local
                        ),
                        delta: item.delta,
                        items: items
                    });
                }
            } catch (e) {
                windowService.logError("1F6B59D4CFBE225", e.message);
            }
        }
    } catch (e) {
        windowService.logError("EE5CCE51140946F", e.message);
        formulas = [];
    }
    return formulas;
};

export const buildCanisterObject = (data) => {
    if (data === null) {
        return new Canister(-1, "", 0, 0, 0, 0, 0, 0, 0, 0, null, [], false);
    }

    let canister = null;

    try {
        canister = new Canister(
            data.index,
            data.name,
            data.currentLevel,
            data.currentLevelMl,
            data.minimumLevel,
            data.minimumLevelMl,
            data.warningLevel,
            data.warningLevelMl,
            data.capacity,
            data.capacityMl,
            data.cntCode !== null
                ? new Colorant(0, data.cntCode, data.cntCode, data.cntRGB, 0, "", data.cntSpecificWeight)
                : null,
            data.channels,
            data.enabled,
            data.ufiCode,
            data.batchNumber,
            data.thresholdPercent,
            data.barcode,
            data.unitCoefficient
        );
    } catch (e) {
        windowService.logError("25D7305D1C115AB", e.message);
        return null;
    }

    return canister;
};

export const buildColorantPackageObject = (data) => {
    try {
        return new ColorantPackage(
            data.id,
            data.package.displayName,
            data.colorant.name,
            data.package.unitId,
            data.package.nominalAmount,
            data.fillVolume,
            data.fillVolumeMl,
            data.package.isGravimetric,
            mapColorantToObject(data.colorant),
            mapPackageToObject(data.package, data.fillVolume, data.fillVolumeMl)
        );
    } catch (e) {
        windowService.logError("4B62313A8CD4F27", e.message);
        return null;
    }
};

export const buildColorantPackageNullObject = () => {
    try {
        return new ColorantPackage(
            -1,
            "",
            "",
            settings.UNIT_KG_ID,
            0,
            0,
            0,
            true,
            new Colorant(0, "", "", config.DEFAULT_RGB, 0),
            new Package(0, "", "", settings.UNIT_KG_ID, settings.UNIT_KG, 0, 0, 0, true)
        );
    } catch (e) {
        windowService.logError("0BEA1FEB407537F", e.message);
        return null;
    }
};

export const mapColorantPackagesToObjects = (list) => {
    let result = [];

    try {
        let colorantPackage = null;

        for (let item of list) {
            colorantPackage = buildColorantPackageObject(item);

            if (colorantPackage !== null) {
                result.push(colorantPackage);
            }
        }

        return result;
    } catch (e) {
        windowService.logError("C7D81FAEFC2A64E", e.message);
        return [];
    }
};

export const mapStatsToObjects = (list) => {
    let stats = [];

    let quickTintDisplayName = translate("general.quickTint");

    try {
        let item;
        for (item of list) {
            try {
                if (item.colorsStatsTypeId === statsTypes.QUICK_TINT) {
                    stats.push(
                        new Statistics(
                            item.id,
                            quickTintDisplayName,
                            quickTintDisplayName,
                            quickTintDisplayName,
                            quickTintDisplayName,
                            quickTintDisplayName,
                            item.packageDisplayName,
                            item.bippDisplayName,
                            item.dispensedPackageCount,
                            item.packageCount,
                            item.customerName,
                            item.colorRgb,
                            item.timestampUtc,
                            item.customPackageName,
                            item.moreInfo,
                            item.moreInfoInternal,
                            item.hidden,
                            item.colorsStatsTypeId,
                            item.qualityControl,
                            item.qualityControlDate,
                            item.colorants,
                            item.cumulativeColorants,
                            item.originalColorants,
                            item.userLogin,
                            item.buildingSite,
                            item.clientName
                        )
                    );
                } else {
                    stats.push(
                        new Statistics(
                            item.id,
                            item.colorDisplayName,
                            item.colorCode,
                            item.fandeckDisplayName,
                            item.productDisplayName,
                            item.baseDisplayName,
                            item.packageDisplayName,
                            item.bippDisplayName,
                            item.dispensedPackageCount,
                            item.packageCount,
                            item.customerName,
                            item.colorRgb,
                            item.timestampUtc,
                            item.customPackageName,
                            item.moreInfo,
                            item.moreInfoInternal,
                            item.hidden,
                            item.colorsStatsTypeId,
                            item.qualityControl,
                            item.qualityControlDate,
                            item.colorants,
                            item.cumulativeColorants,
                            item.originalColorants,
                            item.userLogin,
                            item.buildingSite,
                            item.clientName
                        )
                    );
                }
            } catch (e) {
                windowService.logError("F5F8D723CEF356D", e.message);
            }
        }
    } catch (e) {
        windowService.logError("76E578DD4837070", e.message);
        return [];
    }

    return stats;
};

export const mapStatCorrectionsToObjects = (list) => {
    let correctionList = [];

    try {
        let item;
        for (item of list) {
            correctionList.push({
                item: new StatisticCorrection(
                    item.item.id,
                    item.item.colorDisplayName,
                    item.item.colorCode,
                    item.item.fandeckDisplayName,
                    item.item.productDisplayName,
                    item.item.baseDisplayName,
                    item.item.packageDisplayName,
                    item.item.bippDisplayName,
                    item.item.dispensedPackageCount,
                    item.item.packageCount,
                    item.item.customerName,
                    item.item.colorRgb,
                    item.item.timestampUtc,
                    item.item.customPackageName,
                    item.item.moreInfo,
                    item.item.moreInfoInternal,
                    item.item.hidden,
                    item.item.colorsStatsTypeId,
                    item.item.qualityControl,
                    item.item.qualityControlDate,
                    item.item.colorants,
                    item.item.cumulativeColorants,
                    item.item.originalColorants,
                    item.item.userLogin,
                    item.item.buildingSite,
                    item.item.clientName,
                    item.item.colorantsByUnits,
                    item.item.cumulativeColorantsByUnits,
                    item.item.originalColorantsByUnits,
                    item.item.parentId
                ),
                nodes: mapStatCorrectionsToObjects(item.nodes)
            });
        }
    } catch (e) {
        windowService.logError("3DABC767F2888B1", e.message);
        return [];
    }

    return correctionList;
};

export const mapKeyboardShortcutsToObjects = (keyboardShortcuts) => {
    if (keyboardShortcuts === null) {
        return {};
    }

    let keyboardShortcutsObject = {};

    try {
        let item;
        for (item in keyboardShortcuts) {
            keyboardShortcutsObject[item] = new KeyboardShortcut(
                item,
                keyboardShortcuts[item].keyCode,
                keyboardShortcuts[item].shift,
                keyboardShortcuts[item].ctrl,
                keyboardShortcuts[item].active
            );
        }
    } catch (e) {
        windowService.logError("4F8AE707437DD5C", e.message);
        return {};
    }

    return keyboardShortcutsObject;
};

export const mapMarginDataToObject = (object) => {
    if (object === undefined || object === null) {
        return null;
    }

    try {
        return new MarginData(
            object.marginId,
            object.itemType,
            object.value,
            object.absValue,
            object.local,
            object.productName,
            object.baseName,
            object.packageName,
            object.colorantName
        );
    } catch (e) {
        windowService.logError("962DF10E0BFD860", e.message);
        return null;
    }
};

export const mapBaseInProductPackageToObject = (item) => {
    if (item === null) {
        return null;
    }

    try {
        return new BaseInProductPackage(
            item.id === null ? config.UNKNOWN_ID : item.id,
            item.displayName === null ? item.product.displayName + " " + item.base.displayName : item.displayName,
            item.fillVolume,
            item.fillVolumeMl,
            new Product(
                item.productId === null ? config.UNKNOWN_ID : item.productId,
                item.productName,
                item.productDisplayName === undefined ? item.productName : item.productDisplayName
            ),
            new Base(
                item.baseId === null ? config.UNKNOWN_ID : item.baseId,
                item.baseName,
                item.baseDisplayName === undefined ? item.baseName : item.baseDisplayName,
                item.density
            ),
            new Package(
                item.packageId === null ? config.UNKNOWN_ID : item.packageId,
                item.packageName,
                item.packageDisplayName,
                item.unit,
                window.storeProvider.getUnitCode(item.unit),
                item.nominalAmount,
                item.fillVolume,
                item.fillVolumeMl,
                item.isGravimetric
            )
        );
    } catch (e) {
        windowService.logError("FC6A6503B74917E", e.message);
        return null;
    }
};

export const mapMatchingBaseInProductPackageToObject = (item, bip) => {
    if (item === null) {
        return null;
    }

    let baseDisplayName = item.baseName;

    if (item.baseDisplayName) {
        baseDisplayName = item.baseDisplayName;
    } else if (bip !== null && bip.baseDisplayName) {
        baseDisplayName = bip.baseDisplayName;
    }

    try {
        return new BaseInProductPackage(
            item.id === null ? 0 : item.id,
            item.displayName === null ? item.productDisplayName + " " + item.baseDisplayName : item.displayName,
            item.fillVolume,
            item.fillVolumeMl,
            new Product(
                item.productId === null ? 0 : item.productId,
                item.productName,
                item.productDisplayName === undefined ? item.productName : item.productDisplayName
            ),
            new Base(item.baseId === null ? 0 : item.baseId, item.baseName, baseDisplayName, item.density),
            new Package(
                item.packageId === null ? 0 : item.packageId,
                item.packageName,
                item.packageDisplayName,
                item.unit,
                window.storeProvider.getUnitCode(item.unit),
                item.nominalAmount,
                item.fillVolume,
                item.fillVolumeMl,
                item.isGravimetric
            )
        );
    } catch (e) {
        windowService.logError("8A9AD81225B07f5", e.message);
        return null;
    }
};

const getCanisterColorant = (colorantList, item) => {
    if (item.cntCode === null) {
        return null;
    }

    let colorant = getterHelper.getObjectByName(colorantList, item.cntCode);

    if (colorant !== null) {
        return colorant.updateCanisterValues(item.cntRGB, item.cntSpecificWeight);
    }

    return new MachineColorant(
        item.index,
        item.cntCode,
        item.cntCode,
        item.cntRGB,
        0,
        settings.UNIT_ML,
        item.cntSpecificWeight
    );
};

export const mapCanistersToObjects = (list, colorantList, canisterList) => {
    if (list === null || !Array.isArray(list)) {
        return [];
    }

    let canisters = [];
    let canister = null;

    try {
        for (let item of list) {
            if (item !== null && item.index !== null) {
                canister = getterHelper.getObjectById(canisterList, item.index);

                canisters.push(
                    new Canister(
                        item.index,
                        item.name,
                        item.currentLevel,
                        item.currentLevelMl,
                        item.minimumLevel,
                        item.minimumLevelMl,
                        item.warningLevel,
                        item.warningLevelMl,
                        item.capacity,
                        item.capacityMl,
                        getCanisterColorant(colorantList, item),
                        item.channels,
                        item.enabled,
                        item.barcode,
                        item.ufiCode,
                        item.batchNumber,
                        item.thresholdPercent,
                        item.unitCoefficient,
                        item.maxDispenseAmount,
                        item.dispenseLogicType,
                        canister?.colorantPackages ?? []
                    )
                );
            }
        }
    } catch (e) {
        windowService.logError("A2FDD60C643485C", e.message);
        return [];
    }

    return canisters;
};

export const mapCanisterBatchNumbersToObjects = (canisterBatchNumbers, canisterList) => {
    let result = {};

    try {
        let canister;
        for (let canisterId of Object.keys(canisterBatchNumbers)) {
            canister = canisterList.find((canister) => canister.id === parseInt(canisterId));

            if (canister !== undefined && canister.colorant !== null) {
                result[canister.colorant.id] = canisterBatchNumbers[canisterId];
            }
        }
    } catch (e) {
        windowService.logError("59EB28B4899220C", e.message);
        return {};
    }

    return result;
};

export const mapFormulaCombination = (item) => {
    let combination = null;

    try {
        combination = {
            product: new Product(
                item.baseInProduct.productId,
                item.baseInProduct.productName,
                item.baseInProduct.productName
            ),
            fandeck: mapFandeckToObject(item.fandeck),
            color: mapColorToObject(item.color),
            base: mapBaseToObject(item.base),
            bipp: mapBaseInProductPackageToObject(item.package)
        };

        if (combination.color !== null) {
            combination.color.local = item.isLocal;
        }
    } catch (e) {
        windowService.logError("829376FD5846F57", e.message);
    } finally {
        if (
            combination.product !== null &&
            combination.fandeck !== null &&
            combination.color !== null &&
            combination.base !== null
        ) {
            return combination;
        }

        return null;
    }
};

export const mapBarcodeItemToObject = (itemType, item) => {
    if (itemType === -1 || item === null) {
        return null;
    }

    if (itemType === itemTypes.BASE_IN_PRODUCT_PACKAGE) {
        return mapBaseInProductPackageToObject(item);
    }

    if (itemType === itemTypes.COLORANT_BATCH_NUMBER) {
        return mapColorantBatchNumberToObject(item);
    }

    if (itemType === itemTypes.COLORANT_PACKAGE) {
        return buildColorantPackageObject(item);
    }

    if (itemType === itemTypes.COLOR) {
        return mapColorToObject(item);
    }

    if (itemType === itemTypes.USER) {
        return mapUserToObject(item);
    }

    if (itemType === itemTypes.COLOR_IN_FANDECK) {
        return mapColorInFandeckToObject(item);
    }

    if (itemType === itemTypes.CUSTOMER) {
        return mapCustomerToObject(item);
    }

    if (itemType === itemTypes.UFI_CODE) {
        return mapUfiCodeToString(item);
    }

    if (itemType === itemTypes.FORMULA_COMBINATION) {
        return mapFormulaCombination(item);
    }

    if (itemType === itemTypes.FORMULA_COMBINATION_FOR_BIPP) {
        return mapFormulaCombination(item);
    }

    return null;
};

export const mapAutotasksToObjects = (autotaskList) => {
    let autotasks = [];

    try {
        let autotask;
        for (autotask of autotaskList) {
            try {
                autotasks.push(
                    new Autotask(
                        autotask.id,
                        autotask.name,
                        autotask.actionType,
                        autotask.startType,
                        autotask.enabled,
                        autotask.canPostpone,
                        autotask.postponeInterval,
                        new Date(autotask.nextRun),
                        autotask.runInterval,
                        autotask.runIntervalParams && autotask.runIntervalParams.days
                            ? autotask.runIntervalParams.days
                            : [],
                        autotask.runIntervalParams && autotask.runIntervalParams.weekdays
                            ? autotask.runIntervalParams.weekdays
                            : [],
                        autotask.minDuration,
                        autotask.volume === undefined ? rcc.RCC_DEFAULT_PURGE_VOLUME : autotask.volume,
                        autotask.formula === undefined ? [] : autotask.formula,
                        autotask.threshold === undefined ? rcc.RCC_DEFAULT_AUTOTASK_THRESHOLD : autotask.threshold,
                        autotask.canisters === undefined ? [] : autotask.canisters,
                        autotask.cleaningTime === undefined ? rcc.RCC_DEFAULT_CLEAN_TIME : autotask.cleaningTime,
                        autotask.channels === undefined ? [] : autotask.channels,
                        autotask.recirculationTime === undefined
                            ? rcc.RCC_DEFAULT_RECIRCULATE_TIME
                            : autotask.recirculationTime,
                        autotask.recirculationSpeed === undefined
                            ? rcc.RCC_DEFAULT_RECIRCULATE_SPEED
                            : autotask.recirculationSpeed,
                        autotask.stirringTime === undefined ? rcc.RCC_DEFAULT_STIR_TIME : autotask.stirringTime,
                        autotask.stirringSpeed === undefined ? rcc.RCC_DEFAULT_STIR_SPEED : autotask.stirringSpeed
                    )
                );
            } catch (e) {
                windowService.logError("4D699DB0EC139DA", e.message);
            }
        }
    } catch (e) {
        windowService.logError("B3A7AB955EC65B6", e.message);
        return [];
    }

    return autotasks;
};

const getValidSmartPurgeRuleByChannel = (list, channelId) => {
    let validRule = null;
    for (let rule of list) {
        if (rule.index === channelId && (validRule == null || rule.id > validRule.id)) {
            validRule = rule;
        }
    }

    return validRule;
};

export const mapSmartPurgeRulesToObjects = (list, channelList) => {
    let rules = [];
    try {
        let activeRule = null;
        for (let channel of channelList) {
            activeRule = getValidSmartPurgeRuleByChannel(list, channel.id);

            if (activeRule !== null) {
                try {
                    rules.push(
                        new SmartPurgeRule(
                            activeRule.id,
                            activeRule.index,
                            activeRule.purgeVolume === undefined
                                ? rcc.RCC_DEFAULT_PURGE_VOLUME
                                : activeRule.purgeVolume,
                            activeRule.minDispensedVolume === undefined ? 0 : activeRule.minDispensedVolume,
                            activeRule.excludePurge === undefined ? false : activeRule.excludePurge,
                            activeRule.note === undefined ? "" : activeRule.note,
                            activeRule.priority === undefined ? 0 : activeRule.priority
                        )
                    );
                } catch (e) {
                    windowService.logError("53D6FB42904EAE7", e.message);
                }
            } else {
                try {
                    rules.push(new SmartPurgeRule(-1, channel.index, rcc.RCC_DEFAULT_PURGE_VOLUME, 0, false, "", 0));
                } catch (e) {
                    windowService.logError("a6f7dd195883342", e.message);
                }
            }
        }
    } catch (e) {
        windowService.logError("C9B42D256D84882", e.message);
        return [];
    }

    return rules;
};

export const mapPurgeStatisticsToObjects = (list) => {
    let stats = [];

    try {
        let stat;
        for (stat of list) {
            try {
                stats.push(
                    new ColorantConsumption(
                        stat.channels === undefined ? {} : stat.channels,
                        stat.value === "Total" ? null : stat.value
                    )
                );
            } catch (e) {
                windowService.logError("3B8544A55155B1B", e.message);
            }
        }
    } catch (e) {
        windowService.logError("FEF051C360BCA8A", e.message);
        return [];
    }

    return stats;
};

export const getValidDatabaseList = (list) => {
    let databaseList = [];

    let db;
    for (db of list) {
        if (db.status === databaseStatuses.OK) {
            databaseList.push(db);
        }
    }

    return databaseList;
};

export const getInvalidDatabaseList = (list) => {
    let databaseList = [];

    let db;
    for (db of list) {
        if (db.status !== databaseStatuses.OK) {
            databaseList.push(db);
        }
    }

    return databaseList;
};

export const getUpdateDatabaseList = (list) => {
    let databaseList = [];

    let db;
    for (db of list) {
        if (
            db.status === databaseStatuses.MAIN_AND_LOCAL_UPDATE_NEEDED ||
            db.status === databaseStatuses.MAIN_UPDATE_NEEDED ||
            db.status === databaseStatuses.MAIN_PARTIAL_UPDATE_AND_LOCAL_UPDATE_NEEDED ||
            db.status === databaseStatuses.MAIN_PARTIAL_UPDATE_NEEDED ||
            db.status === databaseStatuses.LOCAL_UPDATE_NEEDED
        ) {
            databaseList.push(db);
        }
    }

    return databaseList;
};

const formatPrintDataArray = (printData) => {
    let resultData = "";

    let value;
    for (value of printData) {
        if (typeof value !== "number") {
            return printData;
        }

        if (resultData === "") {
            resultData = formatHelper.formatDecimal(value);
        } else {
            resultData += " " + formatHelper.formatDecimal(value);
        }
    }

    return resultData;
};

export const formatPrintData = (printData) => {
    if (printData === null) {
        return null;
    }

    let resultData = {};

    let key;
    for (key in printData) {
        if (typeof printData[key] === "number") {
            if (key.startsWith("price_price_")) {
                resultData[key] = String(printData[key]);
            } else if (key.startsWith("price_")) {
                resultData[key] = formatHelper.formatCurrency(printData[key]);
            } else {
                resultData[key] = formatHelper.formatDecimal(printData[key]);
            }
        } else if (Array.isArray(printData[key])) {
            resultData[key] = formatPrintDataArray(printData[key]);
        } else {
            resultData[key] = printData[key];
        }
    }

    return resultData;
};

export const mapFormulaMoreInfoToObjects = (formula) => {
    if (
        formula === undefined ||
        formula === null ||
        formula.moreInfoDetailed === undefined ||
        formula.moreInfoDetailed === null ||
        !Array.isArray(formula.moreInfoDetailed.formulaNotes)
    ) {
        return [];
    }

    let noteList = [];

    try {
        for (let note of formula.moreInfoDetailed.formulaNotes) {
            try {
                noteList.push(new FormulaMoreInfo(note.id, note.value, note.hasImage));
            } catch (e) {
                windowService.logError("E96C64E7C4D2258", e.message);
            }
        }
    } catch (e) {
        windowService.logError("2A1F88F91E42CB5", e.message);
        return [];
    }

    return noteList;
};

export const mapPaletteColorMoreInfoToObjects = (formula) => {
    if (
        formula === undefined ||
        formula === null ||
        formula.moreInfoParsed === undefined ||
        formula.moreInfoParsed === null ||
        !Array.isArray(formula.moreInfoParsed.formulaNotes)
    ) {
        return [];
    }

    let noteList = [];

    try {
        for (let note of formula.moreInfoParsed.formulaNotes) {
            try {
                noteList.push(new FormulaMoreInfo(note.id, note.value, note.hasImage));
            } catch (e) {
                windowService.logError("B23B851D05C412E", e.message);
            }
        }
    } catch (e) {
        windowService.logError("225771250E7608B", e.message);
        return [];
    }

    return noteList;
};

export const setMenuKeyWords = (item, translatedKeyWords) => {
    if (item.type === componentTypes.SETTINGS_OPTION) {
        return new SettingsItem(
            item.name,
            item.type,
            item.value,
            item.defaultValue,
            item.visible,
            item.editable,
            translatedKeyWords[item.name] === undefined ? [] : translatedKeyWords[item.name]
        );
    }

    let children = [];

    if (Array.isArray(item.items)) {
        let child;
        for (child of item.items) {
            children.push(setMenuKeyWords(child, translatedKeyWords));
        }
    }

    return new MenuItem(item.name, item.type, item.visible, item.defaultText, item.parent, children);
};

const getSettingsMenuRecursive = (item, parent) => {
    if (item.type === componentTypes.SETTINGS_OPTION) {
        return new SettingsItem(item.name, item.type, item.value, item.defaultValue, item.visible, item.editable);
    }

    let children = [];

    let child;
    for (child of item.items) {
        if (item.visible) {
            children.push(getSettingsMenuRecursive(child, item.translateText));
        }
    }

    return new MenuItem(item.translateText, item.type, item.visible, item.defaultText, parent, children);
};

export const mapSettingsMenuToObjects = (settingsMenu) => {
    let items = [];

    let item;
    for (item of settingsMenu) {
        if (item.visible) {
            items.push(getSettingsMenuRecursive(item, resources.MENU_SETTIGNS));
        }
    }

    return new MenuItem(
        resources.MENU_SETTIGNS,
        componentTypes.SETTINGS_MAIN_MENU,
        items.length > 0,
        "Settings",
        null,
        items
    );
};

export const buildKeyboardEvent = (key, keyCode, event = "keydown", ctrlKey = false, shiftKey = false) => {
    return new KeyboardEvent(event, {
        bubbles: true,
        cancelable: true,
        code: key,
        ctrlKey: ctrlKey,
        key: key,
        keyCode: keyCode,
        shiftKey: shiftKey
    });
};

export const copy = (src) => {
    let target = {};
    for (let prop in src) {
        if (src.hasOwnProperty(prop)) {
            target[prop] = src[prop];
        }
    }

    return target;
};

export const sortObjectKeys = (object) => {
    let keys = Object.keys(object);
    let length = keys.length;
    let result = {};

    keys.sort();

    let i;
    for (i = 0; i < length; i++) {
        result[keys[i]] = object[keys[i]];
    }

    return result;
};

export const getCustomServerFlashMessage = (data) => {
    let flashMessageType;
    switch (data.type) {
        case componentTypes.SERVER_DIALOG_INFO:
            flashMessageType = componentTypes.FLASH_MESSAGE_INFO;
            break;
        case componentTypes.SERVER_DIALOG_SUCCESS:
            flashMessageType = componentTypes.FLASH_MESSAGE_SUCCESS;
            break;
        case componentTypes.SERVER_DIALOG_WARNING:
            flashMessageType = componentTypes.FLASH_MESSAGE_WARNING;
            break;
        case componentTypes.SERVER_DIALOG_ERROR:
            flashMessageType = componentTypes.FLASH_MESSAGE_ERROR;
            break;
        default:
            flashMessageType = componentTypes.FLASH_MESSAGE_INFO;
            break;
    }

    return new FlashMessage(flashMessageType, translationHelper.getServerDialogTranslations(data));
};

const getCustomFormattedDialog = (params, response, gamma, size) => {
    try {
        if (gamma) {
            if (response.msgID === rcc.RCC_MESSAGE_PLACE_BASE_CAN) {
                return translate("rccDialog.99", [params[0].split(" / ")[1]]);
            }

            if (response.msgID === rcc.RCC_MESSAGE_VALIDATE_BASE_CAN) {
                return translate("rccDialog.98", [params[1]]);
            }
        }

        if (size === 1 || response.msgID !== rcc.RCC_MESSAGE_PLACE_BASE_CAN) {
            return translate("rccDialog." + response.msgID, params);
        }

        let splitted = translate("rccDialog." + response.msgID, params).split(" / ");

        return (
            <div className="dialog-text-inline">
                {splitted[0]} / <span className="dialog-text-large">{splitted[1]}</span> / {splitted[2]}
            </div>
        );
    } catch (e) {
        windowService.logError("D00C37730EA585B", e.message);
        return response.msgTextFormatted;
    }
};

const translateDialogText = (response, gamma, size = 1) => {
    if (response.msgID === null) {
        return response.msgTextFormatted;
    }

    let translation = null;
    if (response.msgParams.length === 0) {
        translation = translate("rccDialog." + response.msgID);

        if (translation === "rccDialog." + response.msgID) {
            return response.msgTextFormatted;
        }

        return translation;
    }

    let params = {};
    let i = 0;

    for (let param of response.msgParamsFormatted) {
        params[i] = param;
        i++;
    }

    translation = getCustomFormattedDialog(params, response, gamma, size);

    if (typeof translation === "string" && translation.endsWith("." + response.msgID)) {
        return response.msgTextFormatted;
    }

    return translation;
};

const getDialogType = (response) => {
    if (response.progress !== rcc.PROGRESS_NONE) {
        return componentTypes.MACHINE_DIALOG_PROGRESS;
    }

    switch (response.msgID) {
        case rcc.RCC_MESSAGE_WARNING_LEVEL:
        case rcc.RCC_MESSAGE_WARNING_LEVELS:
        case rcc.RCC_MESSAGE_CRITICAL_LEVEL:
        case rcc.RCC_MESSAGE_CRITICAL_LEVELS:
        case rcc.RCC_MESSAGE_REFILL_CANISTER:
        case rcc.RCC_MESSAGE_REFILL_CANISTER_AUTOTASK:
        case rcc.RCC_MESSAGE_REFILL_CANISTER_BY_BARCODE:
        case rcc.RCC_MESSAGE_REFILL_CANISTER_AUTOTASK_BY_BARCODE:
            return componentTypes.MACHINE_DIALOG_REFILL;
        case rcc.RCC_MESSAGE_DISPENSING:
        case rcc.RCC_MESSAGE_DISPENSING_NO_BUTTONS:
        case rcc.RCC_MESSAGE_QUICK_TINT:
        case rcc.RCC_MESSAGE_QUICK_TINT_NO_BUTTONS:
            return componentTypes.MACHINE_DIALOG_PROGRESS;
        default:
            if (response.msgIcon === rcc.RCC_ICON_ERROR) {
                return componentTypes.MACHINE_DIALOG_ERROR;
            }

            if (response.msgIcon === rcc.RCC_ICON_WARNING) {
                return componentTypes.MACHINE_DIALOG_WARNING;
            }

            return componentTypes.MACHINE_DIALOG_INFO;
    }
};

const getDialogParams = (response) => {
    if (response.msgParams.length === 0) {
        return [];
    }

    if (!Array.isArray(response.msgParams[0])) {
        return response.msgParams;
    }

    if (response.msgParams[0].length > 0) {
        return response.msgParams[0][0];
    }

    return [];
};

const getDialogMessageCode = (message, state) => {
    if (state.machine.messageCodeList === null) {
        return String(message);
    }

    if (state.machine.messageCodeList[message] === undefined) {
        return String(message);
    }

    return state.machine.messageCodeList[message];
};

export const getCustomUnitDialogText = (dialog, unit, allColorantList, shotsName, shotsCoef) => {
    if (dialog === null) {
        return "";
    }

    let params = null;
    let displayUnit = unit === settings.UNIT_SHOT ? shotsName : unit;

    if (
        dialog.message === rcc.RCC_MESSAGE_FORMULA_OVERFILL_RULE_VOL ||
        dialog.message === rcc.RCC_MESSAGE_FORMULA_OVERFILL_RULE_GRAV
    ) {
        let validUnit = unit;

        if (
            dialog.message === rcc.RCC_MESSAGE_FORMULA_OVERFILL_RULE_VOL &&
            unit !== settings.UNIT_ML &&
            unit !== settings.UNIT_SHOT
        ) {
            validUnit = settings.UNIT_ML;
            displayUnit = settings.UNIT_ML;
        }

        if (
            dialog.message === rcc.RCC_MESSAGE_FORMULA_OVERFILL_RULE_GRAV &&
            unit !== settings.UNIT_G &&
            unit !== settings.UNIT_KG
        ) {
            validUnit = settings.UNIT_G;
            displayUnit = settings.UNIT_G;
        }

        params = [
            formatHelper.formatDecimal(
                conversionHelper.convertByUnits(dialog.params[0], dialog.params[1], validUnit, -1, shotsCoef)
            ),
            displayUnit,
            formatHelper.formatDecimal(
                conversionHelper.convertByUnits(dialog.params[2], dialog.params[3], validUnit, -1, shotsCoef)
            ),
            displayUnit
        ];

        return translate("rccDialog." + rcc.RCC_MESSAGE_FORMULA_OVERFILL_RULE_VOL, params);
    }

    if (dialog.meta === null || dialog.meta.volume === undefined || dialog.meta.cntCode === undefined) {
        return translate("rccDialog." + rcc.RCC_MESSAGE_MANUAL_COLORANT_ADDITION, ["0", displayUnit, "?"]);
    }

    let value = conversionHelper.convertAmountByColorantName(
        dialog.meta.volume,
        settings.UNIT_ML,
        unit,
        dialog.meta.cntCode,
        allColorantList,
        shotsCoef
    );

    params = [formatHelper.formatDecimal(value), displayUnit, dialog.meta.cntCode];

    return translate("rccDialog." + rcc.RCC_MESSAGE_MANUAL_COLORANT_ADDITION, params);
};

const mapRccDialogToObject = (response, state) => {
    let type = getDialogType(response);
    let messageCode = getDialogMessageCode(response.msgID, state);

    if (response.progress === rcc.PROGRESS_NONE) {
        if (type === componentTypes.MACHINE_DIALOG_INFO) {
            return new InfoDialog(
                response.dialogID,
                type,
                translateDialogText(response, state.module.gammaSoftware, state.settings.searchPanelBaseSize),
                response.msgID,
                messageCode,
                response.msgIcon,
                response.msgBtns,
                getDialogParams(response),
                response.msgMeta,
                windowService.getRccHelpMessage(
                    state.settings.locale,
                    state.rcc.machine.typeName,
                    response.msgID,
                    messageCode
                )
            );
        }

        return new Dialog(
            response.dialogID,
            type,
            translateDialogText(response, state.module.gammaSoftware),
            response.msgID,
            messageCode,
            response.msgIcon,
            response.msgBtns,
            getDialogParams(response),
            response.msgMeta
        );
    }

    return new ProgressDialog(
        response.dialogID,
        type,
        translateDialogText(response, state.module.gammaSoftware),
        response.msgID,
        messageCode,
        response.msgIcon,
        response.msgBtns,
        getDialogParams(response),
        response.msgMeta,
        response.progress,
        []
    );
};

export const createDialog = (response, state) => {
    let dialog = mapRccDialogToObject(response, state);

    if (
        dialog.message === rcc.RCC_MESSAGE_MANUAL_COLORANT_ADDITION ||
        dialog.message === rcc.RCC_MESSAGE_FORMULA_OVERFILL_RULE_VOL ||
        dialog.message === rcc.RCC_MESSAGE_FORMULA_OVERFILL_RULE_GRAV
    ) {
        return dialog.updateText(
            getCustomUnitDialogText(
                dialog,
                state.settings.manualAdditionUnit,
                state.global.allColorantList,
                state.settings.shotsName,
                state.settings.shotsCoef
            )
        );
    }

    return dialog;
};

const translateSpectroDialogText = (response) => {
    if (response.msgID === null) {
        return response.msgTextFormatted;
    }

    let translation = null;
    if (response.msgParams.length === 0) {
        translation = translate("rccSpectroDialog." + response.msgID);

        if (Number(translation) === response.msgID) {
            return response.msgTextFormatted;
        }

        return translation;
    }

    let params = {};
    let i = 0;
    let param;
    for (param of response.msgParams) {
        params[i] = param;
        i++;
    }

    translation = translate("rccSpectroDialog." + response.msgID, params);

    if (Number(translation) === response.msgID) {
        return response.msgTextFormatted;
    }

    return translation;
};

const getSpectroDialogType = (response) => {
    if (response.msgIcon === rcc.RCC_ICON_ERROR) {
        return componentTypes.SPECTRO_DIALOG_ERROR;
    }

    if (response.msgIcon === rcc.RCC_ICON_WARNING) {
        return componentTypes.SPECTRO_DIALOG_WARNING;
    }

    return componentTypes.SPECTRO_DIALOG_INFO;
};

export const createSpectroDialog = (response) => {
    return new Dialog(
        response.dialogID,
        getSpectroDialogType(response),
        translateSpectroDialogText(response),
        response.msgID,
        String(response.msgID),
        response.msgIcon,
        response.msgBtns,
        response.msgParams,
        null
    );
};

export const createMessageDialog = (type, text, statusText, params) => {
    return new MessageDialog(type, text, statusText, params);
};

const findColorant = (colorants, name) => {
    let colorant;
    for (colorant of colorants) {
        if (colorant.name === name) {
            return colorant;
        }
    }

    return null;
};

const findCanisterById = (canisters, id) => {
    let canister;
    for (canister of canisters) {
        if (canister.id === id) {
            return canister;
        }
    }

    return null;
};

const findCanisterByName = (canisters, name) => {
    let canister;
    for (canister of canisters) {
        if (canister.colorant !== null && canister.colorant.name === name) {
            return canister;
        }
    }

    return null;
};

const findChannel = (channels, index) => {
    let channel;
    for (channel of channels) {
        if (channel.id === index) {
            return channel;
        }
    }

    return null;
};

const getCanisterRgb = (canister, colorant) => {
    if (canister !== null && canister.colorant !== null && canister.colorant.rgb !== null) {
        return canister.colorant.rgb;
    }

    if (colorant !== null) {
        return colorant.rgb;
    }

    return config.DEFAULT_RGB;
};

export const mapDialogProgressItems = (response, colorants, canisters, channels) => {
    try {
        let current = null;
        let progress = null;
        let channel = null;
        let colorant = null;

        let actionProgress = [];

        switch (response.task) {
            case rcc.PROGRESS_DISPENSE:
                if (
                    response.colorants === undefined ||
                    response.colorants === null ||
                    !Array.isArray(response.colorants.items)
                ) {
                    return [];
                }

                if (
                    response.current === undefined ||
                    response.current === null ||
                    !Array.isArray(response.current.colorants)
                ) {
                    current = [];
                } else {
                    current = response.current.colorants;
                }

                for (progress of response.colorants.items) {
                    colorant = findColorant(colorants, progress.cntCode);

                    actionProgress.push(
                        new MachineActionProgress(
                            colorant === null ? -1 : colorant.id,
                            progress.cntCode,
                            getCanisterRgb(findCanisterByName(canisters, progress.cntCode), colorant),
                            progress.percentage,
                            current.includes(progress.cntCode),
                            progress.done
                        )
                    );
                }

                return actionProgress;
            case rcc.PROGRESS_PURGE:
                if (
                    response.channels === undefined ||
                    response.channels === null ||
                    !Array.isArray(response.channels.items)
                ) {
                    return [];
                }

                if (
                    response.current === undefined ||
                    response.current === null ||
                    !Array.isArray(response.current.channels)
                ) {
                    current = [];
                } else {
                    current = response.current.channels;
                }

                for (progress of response.channels.items) {
                    channel = findChannel(channels, progress.index);
                    colorant = channel === null ? null : channel.colorant;

                    actionProgress.push(
                        new MachineActionProgress(
                            channel === null ? -1 : channel.id,
                            colorant === null ? null : colorant.name,
                            colorant === null || colorant.rgb === null ? config.DEFAULT_RGB : colorant.rgb,
                            progress.percentage,
                            current.includes(progress.index),
                            progress.done
                        )
                    );
                }

                return actionProgress;
            case rcc.PROGRESS_CLEAN:
            case rcc.PROGRESS_RECIRCULATE:
                if (
                    response.channels === undefined ||
                    response.channels === null ||
                    !Array.isArray(response.channels.items)
                ) {
                    return [];
                }

                if (
                    response.current === undefined ||
                    response.current === null ||
                    !Array.isArray(response.current.channels)
                ) {
                    current = [];
                } else {
                    current = response.current.channels;
                }

                for (progress of response.channels.items) {
                    channel = findChannel(channels, progress.index);
                    colorant = channel === null ? null : channel.colorant;

                    actionProgress.push(
                        new MachineActionProgress(
                            channel === null ? -1 : channel.id,
                            colorant === null ? null : colorant.name,
                            colorant === null || colorant.rgb === null ? config.DEFAULT_RGB : colorant.rgb,
                            progress.done ? 1 : 0,
                            current.includes(progress.index),
                            progress.done
                        )
                    );
                }

                return actionProgress;
            case rcc.PROGRESS_STIR:
                if (
                    response.canisters === undefined ||
                    response.canisters === null ||
                    !Array.isArray(response.canisters.items)
                ) {
                    return [];
                }

                if (
                    response.current === undefined ||
                    response.current === null ||
                    !Array.isArray(response.current.canisters)
                ) {
                    current = [];
                } else {
                    current = response.current.canisters;
                }

                let canister = null;
                for (progress of response.canisters.items) {
                    canister = findCanisterById(canisters, progress.index);
                    colorant = canister === null || canister.colorant === null ? null : canister.colorant;

                    actionProgress.push(
                        new MachineActionProgress(
                            canister === null ? -1 : canister.id,
                            colorant === null ? null : colorant.name,
                            colorant === null || colorant.rgb === null ? config.DEFAULT_RGB : colorant.rgb,
                            progress.done ? 1 : 0,
                            current.includes(progress.index),
                            progress.done
                        )
                    );
                }

                return actionProgress;
            default:
                return actionProgress;
        }
    } catch (e) {
        windowService.logError("E8C69B8B9EE0A8E", e.message);
        return [];
    }
};

export const mapFormatDataImportToObjects = (list) => {
    let formats = [];

    try {
        let item;
        for (item of list) {
            try {
                let files = mapFileFormatToObjects(item.files);
                let options = mapFormatOptionToObjects(item.options);
                formats.push(new FormatDataImport(item.name, item.code, files, options));
            } catch (e) {
                windowService.logError("80C25FB7081C7F3", e.message);
            }
        }

        if (formats.length > 0) {
            let format = formats[0].updateActive(true);
            formats[0] = format;
        }

        return formats;
    } catch (e) {
        windowService.logError("38D057B86110FB6", e.message);
        return [];
    }
};

export const mapFileFormatToObjects = (list) => {
    let files = [];

    try {
        let item;
        for (item of list) {
            try {
                files.push(
                    new FileFormat(
                        item.key,
                        item.name,
                        item.contentKey,
                        item.pathKey,
                        item.fileType,
                        item.filter ? item.filter.extension.substr(2) : "",
                        item.filter ? item.filter.name : "",
                        item.mandatory
                    )
                );
            } catch (e) {
                windowService.logError("AE4B804A3E523D2", e.message);
            }
        }

        return files;
    } catch (e) {
        windowService.logError("1E4535F97F76F9B", e.message);
        return [];
    }
};

export const mapFormatOptionToObjects = (list) => {
    let options = [];

    try {
        let item;
        for (item of list) {
            try {
                options.push(
                    new FormatOption(
                        item.key,
                        item.name,
                        item.default ? [item.default] : [],
                        item.values ? item.values : [],
                        item.optionType,
                        item.type
                    )
                );
            } catch (e) {
                windowService.logError("D8C3116814E5720", e.message);
            }
        }

        return options;
    } catch (e) {
        windowService.logError("2D981D18D5F078D", e.message);
        return [];
    }
};

export const scaleColorantList = (list, scaling) => {
    let result = [];

    let colorant;
    for (colorant of list) {
        result.push(colorant.updateAmount(colorant.amount * scaling));
    }

    return result;
};

export const unscaleColorantList = (list, scaling) => {
    let result = [];

    let colorant;
    for (colorant of list) {
        result.push(colorant.updateAmount(colorant.amount / scaling));
    }

    return result;
};

export const getAmountByColorant = (list, colorant) => {
    if (colorant === null) {
        return 0;
    }

    let c;
    for (c of list) {
        if (c.name === colorant.name) {
            return c.amount;
        }
    }

    return 0;
};

export const getColorantListDifferences = (colorantList, modifiedColorantList) => {
    let diff = [];

    let original;
    for (original of colorantList) {
        if (getterHelper.getObjectByName(modifiedColorantList, original.name) === null) {
            diff.push(original.setEmpty());
        }
    }

    if (diff.length > 0) {
        return modifiedColorantList.concat(diff);
    }

    return modifiedColorantList;
};

export const clone = (source) => {
    let target = {};
    for (let prop in source) {
        if (source.hasOwnProperty(prop)) {
            target[prop] = source[prop];
        }
    }

    return target;
};

export const mapHelperConfiguration = (data) => {
    try {
        return new HelperConfiguration(
            data.Auto.check,
            data.Auto.download,
            data.Auto.force_install === undefined ? false : data.Auto.force_install,
            data.Auto.install,
            data.Auto.install_delay,
            data.Auto.install_new_dbs,
            data.Auto.night_check,
            data.Auto.night_check_interval,
            data.Auto.night_check_start,
            data.Auto.night_check_end,
            data.Auto.interval,
            data.Auto.reboot_delay,
            data.Server.port,
            data.Web.host,
            data.Web.port
        );
    } catch (e) {
        windowService.logError("7494A626A28290D", e.message);
        return null;
    }
};

const getHelperDialogType = (response) => {
    if (response.msgIcon === helper.HELPER_ICON_ERROR) {
        return componentTypes.HELPER_DIALOG_ERROR;
    }

    if (response.msgIcon === helper.HELPER_ICON_WARNING) {
        return componentTypes.HELPER_DIALOG_WARNING;
    }

    return componentTypes.HELPER_DIALOG_INFO;
};

const translateHelperDialogText = (response) => {
    if (response.msgID === null) {
        return response.msgTextFormatted;
    }

    let translation = null;

    if (response.msgParams.length === 0) {
        translation = translate("helperDialog." + response.msgID);

        if (Number(translation) === response.msgID) {
            return response.msgTextFormatted;
        }

        return translation;
    }

    let params = {};
    let i = 0;
    let param;
    for (param of response.msgParams) {
        params[i] = param;
        i++;
    }

    translation = translate("helperDialog." + response.msgID, params);

    if (Number(translation) === response.msgID) {
        return response.msgTextFormatted;
    }

    return translation;
};

export const createHelperDialog = (response) => {
    try {
        return new Dialog(
            response.dialogID,
            getHelperDialogType(response),
            translateHelperDialogText(response),
            response.msgID,
            String(response.msgID),
            response.msgIcon,
            response.msgBtns,
            response.msgParams,
            response.msgMeta
        );
    } catch (e) {
        windowService.logError("C70FFF7A3EBA6EF", e.message);
        return null;
    }
};

export const mapManualColorantsToObjects = (colorantList, formula) => {
    let colorants = [];

    try {
        let colorant, manualColorant;
        for (colorant of colorantList) {
            try {
                manualColorant = getterHelper.getObjectByIndex(formula, "cntCode", colorant.name);

                colorants.push(
                    new ManualColorant(
                        colorant.id,
                        colorant.name,
                        colorant.displayName,
                        colorant.rgb,
                        settings.UNIT_MANUAL,
                        colorant.density,
                        colorant.ufi,
                        colorant.validationResult,
                        colorant.warningText,
                        colorant.dispensingLogic,
                        colorant.correction,
                        colorant.amount,
                        manualColorant === null ? "" : manualColorant.pump1,
                        manualColorant === null ? "" : manualColorant.pump2
                    )
                );
            } catch (e) {
                windowService.logError("354338FF5AA0B1A", e.message);
            }
        }
    } catch (e) {
        windowService.logError("DD94A3CD3FAEE46", e.message);
        return [];
    }

    return colorants;
};

export const mapDownloadingFileToObject = (id, fileName, fileType, length) => {
    try {
        let downloadingFileName = formatHelper.formatFileName(fileName, fileType);

        return new DownloadingFile(
            id,
            downloadingFileName,
            windowService.getTempPathForCustomFileName(downloadingFileName),
            length,
            length > config.SERVER_CHUNK_SIZE ? config.SERVER_CHUNK_SIZE : length
        );
    } catch (e) {
        return null;
    }
};

export const buildSetupSteps = () => {
    return [
        new Step(1, setup.LANGUAGE, true, true, true),
        new Step(2, setup.LICENSING, true, false, false),
        new Step(3, setup.DATABASE, false, false, false),
        new Step(4, setup.LOGIN, false, false, false),
        new Step(5, setup.MACHINE, false, false, false),
        new Step(6, setup.PRINTER, false, false, false),
        new Step(7, setup.SPECTRO, false, false, false),
        new Step(8, setup.SETTINGS, false, false, false),
        new Step(9, setup.SUMMARY, false, false, false)
    ];
};

export const mapPrintTasksToObjects = (printTasks) => {
    if (!Array.isArray(printTasks)) {
        return [];
    }

    let result = [];

    try {
        for (let task of printTasks) {
            result.push(
                new PrintTask(
                    task.index,
                    task.name,
                    task.printer,
                    task.template,
                    task.action,
                    task.copies,
                    task.preview,
                    task.variable
                )
            );
        }
    } catch (e) {
        windowService.logError("39B29437CBFBEA4", e.message);
        return [];
    }

    return result;
};

export const mapFileTransferInTaskToObject = (
    name,
    format,
    formatOptions,
    protocol,
    protocolOptions,
    interval,
    notification
) => {
    try {
        return new FileTransferInTask(name, format, formatOptions, protocol, protocolOptions, interval, notification);
    } catch (e) {
        windowService.logError("3575776C4DA57A1A", e.message);
        return null;
    }
};

export const mapFileTransferOutTaskToObject = (name, format, formatOptions, protocol, protocolOptions, startType) => {
    try {
        return new FileTransferOutTask(name, format, formatOptions, protocol, protocolOptions, startType);
    } catch (e) {
        windowService.logError("43F2206C4F4ACA0", e.message);
        return null;
    }
};

export const mapFileTransferInTasksToObjects = (tasks) => {
    if (tasks === null) {
        return [];
    }

    let result = [];
    let taskObject = null;

    try {
        for (let task of tasks) {
            taskObject = mapFileTransferInTaskToObject(
                task.name,
                task.format,
                task.format_options,
                task.protocol,
                task.protocol_options,
                task.interval,
                task.notification
            );

            if (taskObject !== null) {
                result.push(taskObject);
            }
        }
    } catch (e) {
        windowService.logError("C66389A690A565B", e.message);
        return [];
    }

    return result;
};

export const mapFileTransferOutTasksToObjects = (tasks) => {
    if (tasks === null) {
        return [];
    }

    let result = [];
    let taskObject = null;

    try {
        for (let task of tasks) {
            taskObject = mapFileTransferOutTaskToObject(
                task.name,
                task.format,
                task.format_options,
                task.protocol,
                task.protocol_options,
                task.start_type
            );

            if (taskObject !== null) {
                result.push(taskObject);
            }
        }
    } catch (e) {
        windowService.logError("6172AAC25836B45", e.message);
        return [];
    }

    return result;
};

export const buildColorantPricingTable = (
    priceDefinition,
    localPrices,
    priceGroups,
    margins,
    markups,
    discounts,
    marginList
) => {
    try {
        return new ColorantPricingTable(
            priceDefinition,
            localPrices,
            priceGroups,
            margins,
            markups,
            discounts,
            marginList
        );
    } catch (e) {
        return null;
    }
};

export const buildBaseInProductPricingTable = (
    priceDefinition,
    localPrices,
    margins,
    markups,
    discounts,
    marginList
) => {
    try {
        return new BaseInProductPricingTable(priceDefinition, localPrices, margins, markups, discounts, marginList);
    } catch (e) {
        return null;
    }
};

export const buildBaseInProductPackagePricingTable = (
    priceDefinition,
    localPrices,
    margins,
    markups,
    discounts,
    marginList
) => {
    try {
        return new BaseInProductPackagePricingTable(
            priceDefinition,
            localPrices,
            margins,
            markups,
            discounts,
            marginList
        );
    } catch (e) {
        return null;
    }
};

export const mapFormulaValidationErrorsToObject = (
    errors,
    machineValidationAll,
    totalCntAmounts,
    overfill,
    minDosableAmount,
    batchNumbers,
    colorantBaseRules,
    availableColorantAmounts
) => {
    try {
        return new FormulaValidation(
            Array.isArray(errors) ? errors : [],
            machineValidationAll,
            totalCntAmounts,
            overfill,
            minDosableAmount,
            batchNumbers,
            colorantBaseRules,
            availableColorantAmounts
        );
    } catch (e) {
        windowService.logError("0A4E1471D08C84F", e.message);
        return new FormulaValidation(
            [],
            rcc.RCC_VALIDATION_OK,
            totalCntAmounts,
            overfill,
            minDosableAmount,
            batchNumbers,
            colorantBaseRules,
            availableColorantAmounts
        );
    }
};

export const createNextTabObject = (index, tabType) => {
    return new Tab(index, translationHelper.translateTabName(tabType), tabType, null, index);
};

export const mapStockItemToObject = (itemType, item) => {
    if (item === null) {
        return null;
    }

    if (itemType === itemTypes.COLORANT_PACKAGE) {
        return buildColorantPackageObject(item);
    }

    return null;
};

export const createStockItemObject = (stockItemData) => {
    try {
        return new StockItem(
            stockItemData.currentValue,
            stockItemData.minValue,
            stockItemData.ordered,
            stockItemData.prediction,
            stockItemData.itemTypeId,
            mapStockItemToObject(stockItemData.itemTypeId, stockItemData.item),
            stockItemData.database
        );
    } catch (e) {
        windowService.logError("482A3E76FA6D471", e.message);
        return null;
    }
};

export const mapStockItemsToObjects = (stockData) => {
    let result = [];

    try {
        let stockItem = null;

        for (let data of stockData) {
            stockItem = createStockItemObject(data);

            if (stockItem !== null) {
                result.push(stockItem);
            }
        }

        return result;
    } catch (e) {
        windowService.logError("386E729BCBAA6BA", e.message);
        return [];
    }
};

export const formatStockResponseData = (stockData) => {
    let result = {};

    result.colorantPackages = mapStockItemsToObjects(stockData);
    /* eslint-disable-next-line no-undef */
    result.databases = [...new Set(result.colorantPackages.map((colorantPackage) => colorantPackage.database))];

    return result;
};

export const buildTableCell = (data = "", className = "", key = undefined, elementProps = {}) => {
    return new TableCell(data, className, key, elementProps);
};

export const buildSelectValue = (data = "", className = "", key = undefined, elementProps = {}) => {
    return new SelectValue(data, className, key, elementProps);
};

export const buildTableRow = (
    data = [],
    className = "",
    key = undefined,
    elementProps = {},
    subRows = null,
    expandedSubRows = false
) => {
    return new TableRow(data, className, key, elementProps, subRows, expandedSubRows);
};

export const buildTableColumn = (className = "", sortable = false) => {
    return new TableColumn(className, sortable);
};

export const buildModalSection = (data = "", className = "") => {
    return new ModalSection(data, className);
};

export const buildColorantPackageStock = (
    key = "",
    packageName = "",
    colorantName = "",
    databaseName = "",
    value = 0
) => {
    return new ColorantPackageStock(key, packageName, colorantName, databaseName, value);
};

export const mapBarcodeDataToObject = (data) => {
    try {
        return new Barcode(data.id, data.barcode, data.barcodeType, data.isLocal);
    } catch (e) {
        windowService.logError("F50537F1BA33D97", e.message);
        return null;
    }
};

export const buildDropdownToggleButton = (label = "", className = "", svgBefore = undefined, svgAfter = undefined) => {
    return new DropdownToggleButton(label, className, svgBefore, svgAfter);
};

export const mapLicenseModules = (licenseModules) => {
    if (!Array.isArray(licenseModules)) {
        return [];
    }

    try {
        let moduleList = [];

        for (let module of licenseModules) {
            if (modules.SUPPORTED_MODULES.includes(module.code)) {
                moduleList.push(new Module(module.code, module.data === "null" ? null : module.data));
            } else if (module.code.match(modules.MODULE_MATCHING_REGEX) !== null) {
                moduleList.push(new Module(module.code, null));
            }
        }

        return moduleList;
    } catch (e) {
        windowService.logError("2E9E6132FFD3D95", e.message);
        return [];
    }
};

export const mapColorfileList = (colorfileList) => {
    let result = [];

    try {
        for (let colorfile of colorfileList) {
            result.push(new Colorfile(colorfile.id, colorfile.name));
        }
    } catch (e) {
        windowService.logError("83F227613282283", e.message);
    }

    return result;
};

export const mapColorfileTemplateList = (templateList) => {
    let result = [];

    try {
        for (let template of templateList) {
            result.push(new ColorfileTemplate(template.id, template.name));
        }
    } catch (e) {
        windowService.logError("DFFEEE114031DD3", e.message);
    }

    return result;
};

export const mapMatchingResult = (matching) => {
    let result = [];

    try {
        let colorant,
            colorantAmount,
            colorantList = [];
        for (let match of matching) {
            colorantList = [];
            for (colorantAmount of match.formula) {
                colorant = mapColorantToObject(colorantAmount.colorant);

                if (colorant !== null) {
                    colorant.amount = colorantAmount.volume;
                    colorant.realAmount = colorantAmount.volume;
                    colorantList.push(colorant);
                }
            }

            result.push(
                new MatchingData(
                    colorantList,
                    mapMatchingBaseInProductPackageToObject(match.package, match.baseInProduct),
                    mapFormulaPricesToObjects(match.priceData),
                    match.colorDifferences,
                    match.rgb,
                    match.contrastRatio,
                    match.lrv,
                    match.metamerism,
                    match.metamerismDin
                )
            );
        }
    } catch (e) {
        windowService.logError("05E94A2C9297777", e.message);
    }

    return result;
};

export const mapCorrectionResult = (correction) => {
    try {
        let colorant,
            colorantAmount,
            colorantList = [];
        for (colorantAmount of correction.cumulativeFormula) {
            colorant = mapColorantToObject(colorantAmount.colorant);

            if (colorant !== null) {
                colorant.amount = colorantAmount.volume;
                colorant.realAmount = colorantAmount.volume;
                colorantList.push(colorant);
            }
        }

        for (colorantAmount of correction.formulaAddition) {
            colorant = getterHelper.getObjectByName(colorantList, colorantAmount.colorant.name);

            if (colorant !== null) {
                colorant.correction = colorantAmount.volume;
            }
        }

        return new MatchingData(
            colorantList,
            mapMatchingBaseInProductPackageToObject(correction.package, correction.baseInProduct),
            [],
            correction.colorDifferences,
            correction.rgb,
            correction.contrastRatio,
            correction.lrv,
            correction.metamerism,
            correction.metamerismDin,
            correction.asIsColorDifferences
        );
    } catch (e) {
        windowService.logError("DFB95999F57FC3F", e.message);
    }

    return null;
};

export const mapReformulationResult = (reformulation) => {
    try {
        let colorant,
            colorantAmount,
            colorantList = [];
        for (colorantAmount of reformulation.formula) {
            colorant = mapColorantToObject(colorantAmount.colorant);

            if (colorant !== null) {
                colorant.amount = colorantAmount.volume;
                colorant.realAmount = colorantAmount.volume;
                colorantList.push(colorant);
            }
        }

        return new MatchingData(
            colorantList,
            mapMatchingBaseInProductPackageToObject(reformulation.package, reformulation.baseInProduct),
            [],
            reformulation.colorDifferences,
            reformulation.rgb,
            reformulation.contrastRatio,
            reformulation.lrv,
            reformulation.metamerism,
            reformulation.metamerismDin,
            reformulation.asIsColorDifferences
        );
    } catch (e) {
        windowService.logError("F10022D5A63914A", e.message);
    }

    return null;
};

export const getFlashMessage = (type, text) => {
    return new FlashMessage(type, text);
};

export const getInfoFlashMessage = (text) => {
    return getFlashMessage(componentTypes.FLASH_MESSAGE_INFO, text);
};

export const getSuccessFlashMessage = (text) => {
    return getFlashMessage(componentTypes.FLASH_MESSAGE_SUCCESS, text);
};

export const getWarningFlashMessage = (text) => {
    return getFlashMessage(componentTypes.FLASH_MESSAGE_WARNING, text);
};

export const getErrorFlashMessage = (text) => {
    return getFlashMessage(componentTypes.FLASH_MESSAGE_ERROR, text);
};

export const mapSpectralData = (data) => {
    try {
        return new SpectralData(data.rgb, data.lab, data.xyz, data.rgbHex, data.spectral, data.image);
    } catch (e) {
        windowService.logError("8D88C013EEEBDBD", e.message);
        return null;
    }
};

export const mapSpectralDataToObject = (rgb, lab, xyz, rgbHex, spectral) => {
    try {
        return new SpectralData(rgb, lab, xyz, rgbHex, spectral);
    } catch (e) {
        windowService.logError("165EB297EC7fB6B", e.message);
        return null;
    }
};

export const formatReflData = (spectralCurve) => {
    try {
        return {
            start: spectralCurve[0][0],
            width: spectralCurve[1][0] - spectralCurve[0][0],
            data: spectralCurve
        };
    } catch (e) {
        windowService.logError("56B3D72EFCF96A5", e.message);
        return null;
    }
};

export const mapColorDifferenceToObject = (cieLab1976, cieLab2000, cmc, metamerism) => {
    try {
        return new ColorDifferenceData(cieLab1976, cieLab2000, cmc, metamerism);
    } catch (e) {
        windowService.logError("BB1DF7D4D217724", e.message);
        return null;
    }
};

export const mapTicketToObject = (data) => {
    try {
        return new Ticket(
            data.id,
            data.ticketId,
            data.type,
            data.status,
            Boolean(data.read),
            Boolean(data.active),
            data.subject,
            data.request,
            data.answer,
            data.operator,
            data.resolvedBy,
            data.customer ? mapCustomerToObject(data.customer) : null,
            data.fandeck ? mapFandeckToObject(data.fandeck) : null,
            data.color ? mapColorToObject(data.color) : null,
            data.product ? mapProductToObject(data.product) : null,
            data.base ? mapBaseToObject(data.base) : null,
            data.requestDate,
            data.answerDate,
            data.jsonAttributes,
            data.attachmentsRequest,
            data.attachmentsAnswer,
            data.formulaCombinationId
        );
    } catch (e) {
        windowService.logError("301888B90B8A2B9", e.message);
        return null;
    }
};

export const mapServiceDepartments = (data) => {
    let departments = [];

    try {
        for (let department of data) {
            departments.push(new ServiceDepartment(department.departmentId, department.name));
        }
    } catch (e) {
        windowService.logError("EE34DE69B515526", e.message);
        return [];
    }

    return departments;
};

export const mapHistoricalFormulas = (historicalList, shotsName, colorantList, colorantPanelOrderBy) => {
    if (!Array.isArray(historicalList)) {
        return [];
    }

    let result = [];

    try {
        for (let item of historicalList) {
            result.push({
                formula: buildCalculatedFormulaObject(
                    item.color,
                    item.isLocal,
                    item.formula,
                    shotsName,
                    colorantList,
                    colorantPanelOrderBy,
                    item.formulaCombination.id
                ),
                base: mapBaseToObject(item.base, item.baseInProduct),
                bipp: mapBaseInProductPackageToObject(item.package),
                bippList: mapBippToObjects(item.packages),
                moreInfoText: responseHelper.getFormattedMoreInfo(item.formula),
                moreInfoData: mapFormulaMoreInfoToObjects(item.formula),
                historical: item.historical
            });
        }
    } catch (e) {
        windowService.logError("DC72EE45E6F81CB", e.message);
    }

    return result;
};
